  // frontend/src/components/PDFViewer.tsx
import { readSessionRefusal, SessionRefusalShown } from '../utils/sessionSaveGate';
import { useArtifactGate } from './ArtifactGate';
  import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
  import * as pdfjsLib from 'pdfjs-dist';
  import FieldListSidebar, { DIRTY_BADGE, FIELD_LIST_COLLAPSED_KEY, SidebarBadge } from './FieldListSidebar';
  import WorkspaceSplitter from './WorkspaceSplitter';
  import FieldPropertiesSidebar, { FIELD_PROPS_COLLAPSED_KEY, FIELD_PROPS_NAME_INPUT_ID, defaultCharacterBlockConfig, defaultSelectConfig } from './FieldPropertiesSidebar';
  import LoadSessionDialog from './LoadSessionDialog';
  import { FieldSortPrefs, ReadingOrderMode } from '../utils/fieldSort';
  import { getFieldValidationErrors } from '../utils/fieldValidation';
  import { FieldCoordinates } from '../types/codeGeneration';
  import { getApiUrl } from '../config/api-config';
  import { getStoredLicenseKeyHash, getStoredUserEmail } from '../services/LicenseService';
  import { useSession, singleFieldEquals } from '../contexts/SessionContext';
  import { useAuth } from '../contexts/AuthContext';
  import { useCollapsiblePanel } from '../hooks/useCollapsiblePanel';

  // Configure PDF.js worker - served locally (copied from pdfjs-dist into public/ by
  // scripts/copy-pdf-worker.js on prestart/prebuild) so it works offline / air-gapped.
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
  
  // Set PDF.js to be more permissive for development
  pdfjsLib.GlobalWorkerOptions.workerPort = null;

  // Character block configuration constants live in FieldPropertiesSidebar,
  // which owns the character-block editor now.

  /* ------------------------------------------------------------------------
   * THE THREE SURFACES — list, canvas, properties — SHARE THE ROW (W3).
   *
   * This retires the properties BAND and, with it, the band-wins/page-wins
   * height rule that used to be argued at length here, and the retirement is
   * explicit rather than another swing of the pendulum. The history, so the
   * fourth agent does not re-fight it:
   *
   *   1. OVERLAY (until 18 Sep 2026): panels floated over the page. Killed by
   *      QA — "the field properties dropdown completely covers the PDF
   *      view... making the view of the live property field changes
   *      impossible." A panel that COVERS the page ends the task, because the
   *      whole point of editing a field's properties is WATCHING THE FIELD
   *      CHANGE ON THE PAGE.
   *   2. BAND (18 Sep – Oct 2026): properties became a horizontal band above
   *      the page, in the flow, paid for in canvas HEIGHT. That forced a
   *      height arbitration between two floors, reversed twice ("band open,
   *      the band wins at a 280px page floor; band closed, the page wins at
   *      504px"), each reversal correct about the cost the other had ignored:
   *      a 29px band shows nothing, and a page squeezed under an open band
   *      "drops too low into the PDF view" (the owner, 6 Oct, approving this
   *      work). The band also hid the Form Fields list on his viewport — the
   *      keyboard path and the only reading-order surface — which is
   *      DEFECT-84 and the defect this layout exists to close.
   *
   * THE RAIL DISSOLVES THE ARBITRATION INSTEAD OF RE-TUNING IT. Properties
   * now sits BESIDE the canvas, full workspace height, scrolling internally,
   * paid for in WIDTH — the plentiful dimension. Both of the old rules'
   * goals hold at once, permanently: the page keeps its 500px+ floor in
   * EVERY state (the open-band 280px floor is gone because nothing borrows
   * page height any more), and the panel shows everything in every state
   * (full height beats any band cap ever shipped). The band-wins rule is not
   * violated by this layout; it is MOOT, because the fight it refereed no
   * longer exists. Do not reintroduce a surface that pays for itself in
   * canvas height without re-reading this paragraph.
   *
   * WHAT THE OCCLUSION LESSON STILL GOVERNS: between ~1024px and ~1440px
   * there is not room for three persistent columns, so properties becomes a
   * right-edge drawer over the CANVAS EDGE ONLY — never over the field list,
   * which REMAINS OPEN at that width as a hard rule (hiding it is DEFECT-84
   * in new clothes). That is a bounded, author-dismissable return of
   * occlusion at the one width where the alternative is hiding a navigation
   * surface instead, and Esc closes it. Below ~1024px the list and the
   * properties panel share ONE rail as labelled tabs: the trade is explicit
   * and author-controlled, not silent.
   *
   * Still true, and still things an author asked for: everything is in the
   * flow at desktop widths, collapsing a panel genuinely returns its width
   * to the page, no gutters are reserved, and DOM order is visual order —
   * list, page, properties, left to right, which is the tab order too
   * (clause 504.2.2, D30).
   * ---------------------------------------------------------------------- */

  /**
   * The floor under the viewer card, in CSS px.
   *
   * Scott: "The PDF Viewer of the actual PDF should be at least 500px tall, if
   * not more." That 500 is enforced as a min-height on the WORK-AREA ROW, as an
   * inline style rather than a Tailwind class so the rule stays legible beside
   * its reason. There is ONE floor now: with the properties band retired (W3 —
   * see the header note), nothing borrows page height any more, so the
   * open-band 280px floor and the state machine that picked between the two
   * are gone. 504 rather than 500 because the floor lands on the scroller's
   * border box and its frame is 2px top and bottom; a 500 floor measured 496.
   *
   * THIS CONSTANT IS THE SAME FLOOR SEEN FROM OUTSIDE THE CARD: 500 for the
   * page, plus the card's own chrome (16px root padding, a ~28px title row, a
   * ~54px toolbar, an 8px gap). The ~32px the retracted band's pill row used
   * to claim is retired with the band; the slack stays in the constant as
   * headroom rather than being shaved, because a marginally taller floor on a
   * sub-650px window costs nothing and re-deriving the number to the pixel is
   * how off-by-a-row bugs get written. KEEP THIS IN STEP with the row's
   * min-height below.
   *
   * Below this the card overflows its window and the document scrolls, and that
   * is the correct trade rather than a failure: the page keeps its 500px.
   */
  const MIN_CARD_PX = 640;

  /**
   * W3 LAYOUT CONSTANTS — the splitter ranges and defaults, in CSS px.
   *
   * The min/max pairs are the "nothing can be crushed" guarantee stated as
   * arithmetic: no drag or keystroke can take the list below a readable row,
   * the properties rail below its own controls, or — because every maximum is
   * modest against the breakpoints below — the canvas below a working width.
   * The values are COMPONENT STATE ONLY, never localStorage: this file has
   * paid twice for persisted layout state going stale, and a splitter position
   * is a statement about the current window, not about every future one.
   */
  const LIST_MIN_PX = 200;
  const LIST_MAX_PX = 440;
  const LIST_DEFAULT_PX = 280;
  const PROPS_MIN_PX = 280;
  const PROPS_MAX_PX = 560;
  const PROPS_DEFAULT_PX = 340;
  /**
   * The rail width at which the properties panel's internal grid may go to
   * two columns. Below this, two columns of ~150px inputs is not a layout —
   * the panel says so in its own `twoColumn` note. Between this and
   * PROPS_MAX_PX the author has explicitly widened the rail, and the
   * see-both-groups-at-once reading returns.
   */
  const PROPS_TWO_COLUMN_PX = 520;

  /**
   * Centre the page horizontally inside the scroll viewport.
   *
   * The viewport now carries horizontal padding reserving room for the rails, so
   * `clientWidth` is NOT the width the page gets - the content box is. Comparing
   * a page against clientWidth makes one that overflows the content box but not
   * the border box look as though it fits, and it is then left flush under a rail
   * with no scroll applied. Both former call sites did exactly that.
   */
  const centreCanvasScroll = (
    container: HTMLDivElement | null,
    canvas: HTMLCanvasElement | null
  ) => {
    if (!container || !canvas) return;
    const style = window.getComputedStyle(container);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const available = container.clientWidth - padLeft - padRight;
    if (canvas.offsetWidth > available) {
      // The page's own centre in scroll coordinates, less half a viewport.
      container.scrollLeft = Math.max(
        0,
        padLeft + canvas.offsetWidth / 2 - container.clientWidth / 2
      );
    } else {
      container.scrollLeft = 0;
    }
  };

  /**
   * The current W3 arrangement, from the same media queries the state below
   * subscribes to. Module scope, not a component closure, so the resize
   * effect's dependency list can honestly be empty — and with a guarded
   * 'columns' default for environments without matchMedia (SSR, bare jsdom).
   */
  const queryWorkspaceMode = (): 'columns' | 'drawer' | 'tabs' => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'columns';
    if (window.matchMedia('(min-width: 1440px)').matches) return 'columns';
    if (window.matchMedia('(min-width: 1024px)').matches) return 'drawer';
    return 'tabs';
  };

  /**
   * Static element ids for the tabbed rail (< ~1024px). Static rather than
   * useId because exactly one editor renders at a time, the tab keydown
   * handler focuses by id, and FIELD_PROPS_NAME_INPUT_ID already set the
   * precedent for this file's one-instance surfaces.
   */
  const EDITOR_TAB_FIELDS_ID = 'pfb-editor-tab-fields';
  const EDITOR_TAB_PROPS_ID = 'pfb-editor-tab-properties';
  const EDITOR_TABPANEL_FIELDS_ID = 'pfb-editor-tabpanel-fields';
  const EDITOR_TABPANEL_PROPS_ID = 'pfb-editor-tabpanel-properties';

  interface PDFViewerProps {
    documentId: string;
    originalFilename: string;
    projectName?: string;
    onFieldSelect?: (coordinates: FieldCoordinates) => void;
    selectedFields?: FieldCoordinates[]; // Receive fields from parent
    onFieldUpdate?: (field: FieldCoordinates) => void; // Send updates to parent
    onFieldDelete?: (fieldId: string) => void; // Send deletion to parent
    // Replace the whole field array with a new order. The array IS the reading
    // order once the author has touched it (D37).
    onFieldsReorder?: (fields: FieldCoordinates[]) => void;
    hasUnsavedChanges?: boolean; // Receive unsaved changes state from parent
    currentSessionId?: string | null; // Receive current session ID from parent
    // currentSessionId points at the session the server auto-created at upload time
    // rather than one the user saved or loaded. It is savable, but nothing has been
    // saved yet, so this is not an "existing session" from the user's point of view.
    isAutoCreatedSession?: boolean;
    onUnsavedChangesUpdate?: (hasChanges: boolean) => void; // Notify parent of local changes
    onLoadSession?: (sessionId: string) => void; // Use parent's session loading
    onSessionSaved?: (sessionId: string, sessionName: string) => void; // Notify parent of saved session
    fieldSortPrefs: FieldSortPrefs; // Shared with LiveFormPreview (lifted to PDFUpload). A VIEW.
    onFieldSortPrefsChange: (prefs: FieldSortPrefs) => void;
    /** The document's reading order mode, which is a different fact from the
     *  sidebar's sort - see utils/fieldSort.ts. Required, not defaulted: the
     *  sidebar states it to the author, and defaulting it here would let a
     *  caller that forgot it tell the author AUTO while sending MANUAL. */
    readingOrderMode: ReadingOrderMode;
    onReadingOrderModeChange: (mode: ReadingOrderMode) => void;
    /**
     * DOC-03. The two document-level accessibility properties, owned by PDFUpload
     * (which draws their controls) and needed here because the Save Session dialog
     * lives in this component. They are REQUIRED, not optional with an '' default:
     * a save that omitted them would write NULL over a title the author had typed,
     * and a compile error is a better guard against that than a comment.
     */
    documentTitle: string;
    documentLanguage: string;
  }

  interface TextElement {
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
    pageNumber: number;
    fontSize: number;
    fontName: string;
    bold: boolean;
    italic: boolean;
    color?: string;
  }

  const PDFViewer: React.FC<PDFViewerProps> = ({
    documentId,
    originalFilename,
    projectName,
    onFieldSelect,
    selectedFields = [], // Default to empty array if not provided
    onFieldUpdate,
    onFieldDelete,
    onFieldsReorder,
    hasUnsavedChanges: parentHasUnsavedChanges = false,
    currentSessionId: parentCurrentSessionId = null,
    isAutoCreatedSession = false,
    onUnsavedChangesUpdate,
    onLoadSession,
    onSessionSaved,
    fieldSortPrefs,
    onFieldSortPrefsChange,
    readingOrderMode,
    onReadingOrderModeChange,
    documentTitle,
    documentLanguage
  }) => {
    // Section 3.5.6: the saved-document cap is enforced server-side. This
    // surfaces the refusal with a way forward instead of a bare failure.
    const { showLimit, showGate } = useArtifactGate();

    const { state: sessionState } = useSession();
    const { state: authState } = useAuth();
    const savedFieldsSnapshot = sessionState.savedFieldsSnapshot;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
    const wheelZoomAccumulatorRef = useRef(0);

    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [scale, setScale] = useState(0.75);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Note: selectedFields state removed - using parent callback instead
    const [fieldAnalysis, setFieldAnalysis] = useState<{[key: string]: TextElement[]}>({});
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState<{x: number, y: number} | null>(null);
    const [currentSelection, setCurrentSelection] = useState<Partial<FieldCoordinates> | null>(null);
    const [interactionMode, setInteractionMode] = useState<'edit' | 'view'>('edit');
    const [spaceHeld, setSpaceHeld] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    // Space-hold gives a momentary View mode without leaving Edit
    const isViewMode = interactionMode === 'view' || spaceHeld;
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    /**
     * The inspector's open/retracted state, remembered across reloads.
     *
     * It lives HERE rather than inside FieldPropertiesSidebar because Esc has to
     * close the panel where the panel covers the canvas edge (the drawer range),
     * and Esc is a window handler in this component - which cannot close a panel
     * whose state it cannot see. useCollapsiblePanel is unchanged and is still
     * the only mechanism; it is simply called one level up. One source of truth,
     * not two.
     *
     * THE STORED KEY KEEPS ITS MEANING ACROSS THE W3 LAYOUTS, deliberately
     * rather than by migration: `true` has always meant "the properties
     * surface is retracted to its pill", and it still does in every mode —
     * rail retracted at three-column widths, drawer closed at drawer widths,
     * and the Properties tab showing its pill below ~1024px. One key, one
     * meaning, three renderings; an author who retracts on a monitor finds
     * the same choice honoured on a laptop.
     */
    const [propsCollapsed, togglePropsCollapsed, dismissPropsTransiently] = useCollapsiblePanel(FIELD_PROPS_COLLAPSED_KEY);

    /**
     * The field list's collapse state, lifted here from inside FieldListSidebar
     * for the same reason the inspector's was: the list-edge splitter below has
     * to disappear when the list retracts (a separator resizing a 48px pill is
     * a control that lies), and this component cannot react to a state it
     * cannot see. Same hook, same key, called one level up; LiveFormPreview's
     * instance keeps the self-contained behaviour.
     */
    const [listCollapsed, toggleListCollapsed] = useCollapsiblePanel(FIELD_LIST_COLLAPSED_KEY);

    /**
     * FULLSCREEN FOCUS MODE (EDITOR_WORKSPACE_PLAN.md §W2): the card takes the
     * whole window — fixed inset-0 — until the author leaves by the toolbar
     * button or Esc.
     *
     * Component state, NEVER localStorage, and that is a decision rather than
     * an omission: fullscreen is a gesture, not a preference. A remembered
     * fullscreen would greet the next visit with a page whose chrome — header,
     * hero, footer — has silently vanished, and this codebase has already paid
     * for stale persisted UI state more than once.
     *
     * The announcement state exists because entering or leaving fullscreen is
     * a layout change a screen-reader user cannot otherwise perceive; it feeds
     * the polite live region beside the selection announcer below.
     */
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenAnnouncement, setFullscreenAnnouncement] = useState('');
    // Stable on purpose: the window Esc handler closes over it, and a new
    // identity every render would churn that listener for no reason.
    const applyFullscreen = React.useCallback((on: boolean) => {
      setIsFullscreen(on);
      setFullscreenAnnouncement(on ? 'Full screen on. Press Escape to leave.' : 'Full screen off.');
    }, []);

    /**
     * While the card owns the window, the page behind it must not scroll — a
     * wheel at the edge of the editor would otherwise pan a document nobody
     * can see. The previous value is restored rather than assumed to be '',
     * so this cannot erase an overflow some other surface set.
     */
    useEffect(() => {
      if (!isFullscreen) return;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }, [isFullscreen]);

    /**
     * The viewer card is as tall as the WINDOW, less whatever stays stuck to the
     * top of it. It is NOT as tall as "the window measured from wherever this
     * card happens to start", which is what it was until QA.
     *
     * THIS IS THE HEIGHT REGRESSION. Scott, on a build whose whole purpose was to
     * add 200px: "The PDF View layout height is shorter, not 200px larger."
     *
     * The previous pass replaced a `calc(100vh - Nrem)` guess with a measurement
     * - window.innerHeight minus this card's own DOCUMENT-space top - and the
     * geometry harness reported +71px at 1280x720. The harness was wrong, and it
     * was wrong for one reason: ITS PAGE CHROME WAS A SINGLE ROW. The real page
     * puts a sticky 64px site header, a ~370px hero block, a ~124px progress and
     * licence row, GenerationControls once any field exists, and the view-toggle
     * row above this card. The card's document top is 600-820px - MORE than a
     * 720px window is tall. So `innerHeight - documentTop` went negative, the
     * Math.max(320) floor caught it, the separate 32rem CSS minHeight then beat
     * that, and the card came out 512px against the 672px the old constant gave.
     * Shorter, exactly as reported, and far worse at 2560x1440.
     *
     * Measuring was the right instinct pointed at the wrong quantity. The chrome
     * above this card SCROLLS AWAY. It is not between the card and the bottom of
     * the window once the author has scrolled to the editor. The only thing that
     * stays there is the sticky site header, so that is the only thing
     * subtracted - and it is measured off the computed style rather than assumed,
     * so this stays correct if the header's height ever changes.
     *
     * THE DOCUMENT SCROLLS, and it always did. The hero block alone is taller
     * than a 720px window; no height for this card can change that. The claim in
     * the last pass that the document never scrolls was an artefact of the same
     * one-row fixture. The author scrolls to the editor once, and it then fills
     * the glass.
     *
     * ONE FLOOR, NOT TWO. MIN_CARD_PX is applied here and there is no CSS
     * minHeight beside it. Two floors fighting over one box is precisely the
     * 900px-vs-90vh defect 00cadac was written to remove; it is not worth
     * rebuilding for tidiness.
     */
    const [cardHeight, setCardHeight] = useState<string>('calc(100vh - 5rem)');
    useLayoutEffect(() => {
      const fit = () => {
        // Whatever is pinned to the top of the viewport is the only chrome still
        // between this card and the bottom of the window. Read off the COMPUTED
        // position, so the stylesheet stays the one place that decides what is
        // sticky and how tall it is.
        let stuck = 0;
        document.querySelectorAll('header, [data-sticky-chrome]').forEach((node) => {
          const el = node as HTMLElement;
          const style = window.getComputedStyle(el);
          const pinnedToTop =
            (style.position === 'sticky' || style.position === 'fixed') &&
            (parseFloat(style.top) || 0) === 0;
          if (pinnedToTop) stuck = Math.max(stuck, el.offsetHeight);
        });
        const BOTTOM_GUTTER_PX = 8;
        setCardHeight(`${Math.max(MIN_CARD_PX, window.innerHeight - stuck - BOTTOM_GUTTER_PX)}px`);
      };
      fit();
      // One more pass after paint: web fonts land a frame late and can change the
      // header's height by a pixel or two.
      const raf = requestAnimationFrame(fit);
      window.addEventListener('resize', fit);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', fit);
      };
    }, []);

    /**
     * WHICH OF THE THREE W3 ARRANGEMENTS IS ON SCREEN. The plan's breakpoints,
     * owner-approved (EDITOR_WORKSPACE_PLAN.md §W3):
     *
     *   - 'columns' (>= 1440px): list | canvas | properties rail, three
     *     persistent in-flow columns. Nothing overlays anything.
     *   - 'drawer' (1024–1439px): list and canvas persist; properties is a
     *     right-edge drawer over the CANVAS EDGE ONLY. The list REMAINS OPEN —
     *     the hard rule at this width, asked and answered by the owner,
     *     because hiding the keyboard path is DEFECT-84 in new clothes. Esc
     *     closes the drawer (see the Esc chain below).
     *   - 'tabs' (< 1024px): list and properties share ONE rail as labelled
     *     tabs beside a canvas that keeps the majority of the width. The
     *     trade is explicit and author-controlled — and it is also the phone
     *     story: professional authoring targets desktop, so small screens get
     *     NOT BROKEN (no sideways page scroll, everything reachable) rather
     *     than optimised.
     *
     * This three-way state RETIRES the old 768px `sideBySide` flag and the
     * stacked layout it guarded. That flag existed to stop the band's
     * "wins when open" height rule from clipping the page out of the card at
     * a 320px viewport (WCAG 1.4.10) — with the band gone nothing borrows
     * page height in any mode, and at 320px the tabbed rail sits BESIDE the
     * page at a capped share of the row rather than stacking above it, so
     * the clipping geometry it measured cannot be rebuilt.
     *
     * matchMedia rather than a resize listener so the component changes state
     * exactly when the stylesheet would, and a guarded default of 'columns'
     * for environments without matchMedia (SSR, bare jsdom).
     */
    const [workspaceMode, setWorkspaceMode] = useState<'columns' | 'drawer' | 'tabs'>(queryWorkspaceMode);
    useEffect(() => {
      if (!window.matchMedia) return;
      const queries = [window.matchMedia('(min-width: 1440px)'), window.matchMedia('(min-width: 1024px)')];
      const onChange = () => setWorkspaceMode(queryWorkspaceMode());
      queries.forEach(mq => mq.addEventListener('change', onChange));
      return () => queries.forEach(mq => mq.removeEventListener('change', onChange));
    }, []);

    /**
     * The splitter-controlled panel widths, in CSS px. COMPONENT STATE ONLY —
     * see the constants block for why these never touch localStorage. In tabs
     * mode listWidth also sizes the shared rail, further capped to a share of
     * the row below so no stored-in-state width can crush the canvas on a
     * narrow window.
     */
    const [listWidth, setListWidth] = useState(LIST_DEFAULT_PX);
    const [propsWidth, setPropsWidth] = useState(PROPS_DEFAULT_PX);

    /**
     * Which surface the shared rail shows below ~1024px. Selecting a field is
     * an act of editing, so it switches the rail to Properties; clearing the
     * selection hands the rail back to the list. A manual tab click between
     * those moments is respected — the effect keys on the SELECTION changing,
     * not on every render.
     */
    const [activeTab, setActiveTab] = useState<'fields' | 'properties'>('fields');
    useEffect(() => {
      if (workspaceMode !== 'tabs') return;
      setActiveTab(selectedFieldId ? 'properties' : 'fields');
    }, [workspaceMode, selectedFieldId]);
    const [isGeneratingForm, setIsGeneratingForm] = useState(false);
    const [generatedForm, setGeneratedForm] = useState<any>(null);
    const [editingField, setEditingField] = useState<FieldCoordinates | null>(null);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [sessionName, setSessionName] = useState('');
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [isSavingSession, setIsSavingSession] = useState(false);
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorNotification, setShowErrorNotification] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showFieldNames, setShowFieldNames] = useState(true);
    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
    const [showLoadDialog, setShowLoadDialog] = useState(false);
    const [availableSessions, setAvailableSessions] = useState<any[]>([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [selectedSessionToLoad, setSelectedSessionToLoad] = useState<string | null>(null);
    
    // Unsaved changes tracking - use parent state and local dialog state
    const hasUnsavedChanges = parentHasUnsavedChanges;
    const currentSessionId = parentCurrentSessionId; // Use session ID from parent
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
    const [deploymentStatus, setDeploymentStatus] = useState('planning');
    const [changeComment, setChangeComment] = useState('');
    const [sessionDescription, setSessionDescription] = useState('');
    const [isEditingExistingSession, setIsEditingExistingSession] = useState(false);
    const [scrollInterval, setScrollInterval] = useState<NodeJS.Timeout | null>(null);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const [lastScale, setLastScale] = useState(0.75);
    const [lastPage, setLastPage] = useState(1);

    // Configuration: Set to true to enable save confirmation dialog
    // Change this to true if you want an additional confirmation step before saving
    const ENABLE_SAVE_CONFIRMATION = false;

    // Initialize editing field when a field is selected
    useEffect(() => {
      if (selectedFieldId) {
        const field = selectedFields.find(f => f.id === selectedFieldId);
        setEditingField(field ? { ...field } : null);
      } else {
        setEditingField(null);
      }
    }, [selectedFieldId, selectedFields]);

    // Detect when parent provides a session ID (editing existing session).
    // An auto-created upload session does not count: the user has not saved
    // anything yet, so the first save is still a Save, not an Update.
    useEffect(() => {
      if (currentSessionId && currentSessionId.trim() !== '' && !isAutoCreatedSession) {
        setIsEditingExistingSession(true);
      } else {
        setIsEditingExistingSession(false);
        setChangeComment(''); // Clear change comment when not editing
      }
    }, [currentSessionId, isAutoCreatedSession]);

    // Helper function to get display name for field
    const getFieldDisplayName = (field: FieldCoordinates, fallbackIndex?: number) => {
      // If field has a valid name that's not loading or empty
      if (field.name && field.name.trim() !== '' && field.name !== 'loading...') {
        return field.name;
      }
      
      // Extract numeric part from field ID (e.g., "field_123" -> "123")
      const idMatch = field.id.match(/\d+/);
      const fieldNumber = idMatch ? idMatch[0] : (fallbackIndex ?? 0) + 1;
      
      // Return field ID as display name
      return `Field_${fieldNumber}`;
    };

    // Per-field dirty badge vs the last persisted snapshot, for the sidebar:
    // New (never saved), Modified (differs from saved), Saved.
    const fieldBadgeById = useMemo(() => {
      const badges: Record<string, SidebarBadge> = {};
      selectedFields.forEach(field => {
        const snapshotField = savedFieldsSnapshot.find(f => f.id === field.id);
        if (!snapshotField) {
          badges[field.id] = DIRTY_BADGE.new;
        } else if (!singleFieldEquals(field as any, snapshotField as any)) {
          badges[field.id] = DIRTY_BADGE.modified;
        } else {
          badges[field.id] = DIRTY_BADGE.saved;
        }
      });
      return badges;
    }, [selectedFields, savedFieldsSnapshot]);

    /**
     * The free space inside the scroll viewport, on both axes, in CSS px.
     *
     * It is simply the content box — no panel overhang to subtract, no
     * reserved gutter to add back; the padding is the whole story. It is read
     * off the computed style rather than assumed, so this keeps working if
     * the padding changes.
     *
     * The ONE overlay exception is the W3 drawer (~1024–1440px), which is
     * deliberately NOT subtracted: it is transient and author-dismissable, a
     * fit is a one-shot gesture, and a fit that shrank the page because a
     * panel happened to be open would re-fit wrong the moment it closed —
     * the "moves the thing being measured" failure the fit buttons' own
     * notes forbid.
     *
     * Shared by both fit buttons so they cannot drift apart.
     */
    const freeViewportSpace = (container: HTMLDivElement) => {
      const style = window.getComputedStyle(container);
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;

      return {
        width: container.clientWidth - padLeft - padRight,
        height: container.clientHeight - padTop - padBottom,
      };
    };

    /**
     * The scale a FIT operation is allowed to ask for.
     *
     * IT USED TO BE Math.min(3, ...) AND THAT WAS A SILENT DEFECT ON EXACTLY THE
     * MONITORS THIS EDITOR IS BUILT FOR. A US Letter page is 612pt wide; the page
     * viewport on a 2560px monitor is about 2410px. Fit width therefore wants
     * 2410 / 612 = 3.94, got 3.0, and rendered the page at 76% of the width with
     * no message and no visible cause. Scott reported it as the page not filling
     * the width, which is precisely what it was - and it looked like a layout bug,
     * because a clamp leaves no trace. The wider the monitor the worse it got, and
     * Fit page had the same ceiling through the same function.
     *
     * 3 IS A CONTROL FOR THE +/- BUTTONS, NOT A PROPERTY OF THE DOCUMENT. It is a
     * sensible end-stop for repeated clicking; it is meaningless as an answer to
     * "how big is this page in this window", which is a measurement. So the two
     * are separate now: the buttons and the ctrl+wheel keep their 0.25-3 range and
     * their 0.25 / 0.05 steps exactly as they were, and a fit may ask for what it
     * actually computed.
     *
     * 8 IS THE NEW CEILING and it is a guard against a pathological input (a page
     * reporting a 1pt width), not a policy. 8x of Letter is a 4896x6336 canvas,
     * which is the largest thing worth allocating; a fit on a real page lands
     * between 0.5 and 4.5 and never approaches it.
     *
     * NO ROUNDING, unlike the button range. Quantising to 0.01 moves a 612pt page
     * by up to 3px, which either leaves a gap or turns on a horizontal scrollbar -
     * both of them "it does not fit" to the eye, on the one operation whose whole
     * job is to fit. The percentage readout rounds for display anyway, so nothing
     * is gained by rounding the value itself. The harness asserts the rendered
     * width equals the viewport's content box to within half a pixel.
     */
    const clampFitScale = (raw: number) => Math.min(8, Math.max(0.25, raw));

    /**
     * Fit the page to the width free beside the panels, ONCE, on request.
     *
     * Fit-width is a button and never a continuous behaviour. Scott is sizing
     * fields to the pixel against a printed form; a page that re-fits itself
     * whenever a panel opens, or the window moves, moves the thing being measured
     * mid-measurement. Zoom stays exactly where it was put until this is pressed.
     */
    const handleFitWidth = async () => {
      const container = containerRef.current;
      if (!container || !pdfDoc) return;
      try {
        const page = await pdfDoc.getPage(currentPage);
        const unscaledWidth = page.getViewport({ scale: 1 }).width;
        if (!unscaledWidth) return;

        const { width: available } = freeViewportSpace(container);
        if (available <= 0) return;

        setScale(clampFitScale(available / unscaledWidth));
      } catch (err) {
        console.error('Fit width failed:', err);
      }
    };

    /**
     * Fit the WHOLE page - both dimensions - into the free space, once.
     *
     * THIS IS THE ANSWER TO "SHOULD THERE BE A HEIGHT SETTING", and it is
     * deliberately not a setting. Scott, 18 Sep: "I am uncertain if there should be
     * a user input to make the vertical view of the PDF be extremely tall."
     *
     * A persistent height control would be a decision the author has to make
     * repeatedly, can set wrong, and then has to remember they set - and it does
     * not answer the actual need anyway. "I want to see the whole page" is a ZOOM
     * operation, and Fit Page is the standard control for it in every PDF tool
     * there is: one press, no state to maintain, correct at any window size and any
     * page size, and it composes with the zoom buttons instead of fighting them. A
     * tall-view setting would also break the single-scroll-region invariant 66ef98d
     * exists to protect, by making the canvas taller than the viewport that owns
     * the scrolling.
     *
     * DO NOT re-propose the setting. If Fit Page is not enough, the missing piece
     * is a fit-to-SELECTION zoom, not a stored height.
     *
     * It scales to the SHORTER constraint - min of the two ratios - because fitting
     * one axis while overflowing the other is not "the whole page".
     */
    const handleFitPage = async () => {
      const container = containerRef.current;
      if (!container || !pdfDoc) return;
      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale: 1 });
        if (!viewport.width || !viewport.height) return;

        const free = freeViewportSpace(container);
        if (free.width <= 0 || free.height <= 0) return;

        setScale(clampFitScale(Math.min(free.width / viewport.width, free.height / viewport.height)));
      } catch (err) {
        console.error('Fit page failed:', err);
      }
    };

    /**
     * FIT PAGE IS THE ZOOM A DOCUMENT OPENS AT. Once, on first load, and never again.
     *
     * Scott, 18 September: "seeing a lot of the PDF page content as a whole would
     * make sense." The old default was a flat 0.75, which shows the top two thirds
     * of a Letter page and nothing else, so the first thing an author did on every
     * document was scroll to find out what they had uploaded.
     *
     * WHY NOT FIT WIDTH, WHICH IS THE OBVIOUS CANDIDATE. Arithmetic, not preference.
     * Letter is 1 : 1.294, so a page fitted to the width of a landscape viewport is
     * 1.294 times the viewport's width tall - and the viewport is WIDER than it is
     * tall. At 1920x1080 the page area is roughly 1600x620, fit width puts the page
     * at 2070px tall, and about 30% of it is on screen. That is not a defect to fix,
     * it is what fitting the long axis to the short one means. The answer every
     * document tool reached is the same one: open zoomed OUT, let the person zoom in
     * where they are working. InDesign, Figma, Acrobat and Preview all do this.
     *
     * FIT WIDTH IS UNCHANGED. It is still a button, still one-shot, and still the
     * right control when the author wants the page as wide as the glass.
     *
     * IT IS A DEFAULT, NOT A BEHAVIOUR. Nothing re-fits on resize, on a panel
     * opening, or on a page change - see handleFitWidth's note on why continuous
     * fitting is wrong for someone sizing fields against a printed form. The ref
     * keys on documentId so this runs once per document, which also means a session
     * load, which restores its own saved scale, is never overridden: the document is
     * already fitted by then and this effect will not fire again for it.
     *
     * TWO FRAMES, because the card's height is set in a layout effect and refined
     * once more after paint (web fonts land a frame late). Fitting against the
     * intermediate viewport would zoom to a slightly wrong number exactly once, and
     * invisibly.
     */
    const autoFitDocRef = useRef<string | null>(null);
    useEffect(() => {
      if (!pdfDoc || !documentId) return;
      if (autoFitDocRef.current === documentId) return;
      autoFitDocRef.current = documentId;
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => { void handleFitPage(); });
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
      // handleFitPage is re-created every render and the ref above makes this
      // idempotent, so listing it would only re-run an effect that returns
      // immediately. The dependency that matters is the document.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc, documentId]);

    // Selection is announced POLITELY rather than by moving focus. A panel that
    // grabs focus when the author clicks a field on the canvas takes the keyboard
    // away from someone who never asked for it; a screen-reader user still has to
    // learn that the inspector's contents changed. Derived during render on
    // purpose - it is a pure function of the current selection, so an effect plus
    // a piece of state would only be a slower way of computing the same string.
    const announcedIndex = selectedFields.findIndex(f => f.id === selectedFieldId);
    const selectionAnnouncement =
      announcedIndex >= 0
        ? `${getFieldDisplayName(selectedFields[announcedIndex], announcedIndex)} selected — ` +
          `${selectedFields[announcedIndex].type}, page ${selectedFields[announcedIndex].pageNumber}`
        : '';

    /**
     * The drawer's open/close announcement, in ITS OWN polite live region —
     * the third such region, and the fullscreen note explains why they do not
     * share: the selection region's text is derived during render as a pure
     * function of the selection, and pushing a layout event through it would
     * corrupt that derivation with state it was built to avoid.
     *
     * Announced only in drawer mode, because only there does the panel COVER
     * something — at three-column widths the rail arriving beside the page is
     * already conveyed by the selection announcement, and narrating every
     * selection twice would be noise. The ref carries the previous visible
     * state so the announcement fires on the TRANSITION, not on every render
     * in which the drawer happens to be open.
     */
    const [drawerAnnouncement, setDrawerAnnouncement] = useState('');
    const drawerVisible = workspaceMode === 'drawer' && !!(selectedFieldId && editingField) && !propsCollapsed;
    const drawerWasVisibleRef = useRef(false);
    useEffect(() => {
      const was = drawerWasVisibleRef.current;
      drawerWasVisibleRef.current = drawerVisible;
      if (drawerVisible && !was) {
        setDrawerAnnouncement('Field properties opened beside the page. Press Escape to close them.');
      } else if (!drawerVisible && was && workspaceMode === 'drawer') {
        setDrawerAnnouncement('Field properties closed.');
      }
    }, [drawerVisible, workspaceMode]);

    // Sidebar selection: also navigate the canvas to the field's page — the
    // canvas overlay only renders fields on the current page, so selecting a
    // field you can't see was a dead end before.
    const handleSidebarFieldSelect = (fieldId: string) => {
      const field = selectedFields.find(f => f.id === fieldId);
      if (field && field.pageNumber && field.pageNumber !== currentPage) {
        setCurrentPage(field.pageNumber);
      }
      setSelectedFieldId(fieldId);
    };

    // Helper function to update the editing field. When the type changes, strip
    // type-specific config (characterBlockConfig/radioGroupConfig/selectConfig)
    // from the previous type so it doesn't ride along on save and trip the backend DTO.
    //
    // Live update: also propagates to the parent (via onFieldUpdate) so the field
    // overlay on the PDF and the LiveFormPreview reflect every keystroke — matching
    // the behavior of the old (retired) draggable properties dialog. Without the parent dispatch,
    // changes only persisted in this local buffer until an explicit save, which made
    // the panel feel broken (typing didn't move the field).
    const updateEditingField = (property: keyof FieldCoordinates, value: any) => {
      if (!editingField) return;
      const updated: any = { ...editingField, [property]: value };
      if (property === 'type' && value !== editingField.type) {
        // Give the new type the config it needs to render, but only if it has
        // none: a field returning to a type it held earlier keeps whatever was
        // tuned then. Character Block in particular drew NOTHING without a
        // config, so choosing it used to look like it had done nothing at all.
        //
        // Configs belonging to other types are deliberately left in place. They
        // used to be deleted here, which meant one wrong click in this dropdown
        // silently destroyed a tuned layout with no way back and no indication
        // it had happened. Editing should be forgiving; a dormant config costs
        // nothing, because every generated-code path switches on the field's
        // type (CodeGenerationService's character_block / CHARACTER_BLOCK
        // cases), so an unused config is inert data rather than behaviour.
        //
        // Stripping configs that no longer match the field's type belongs at
        // save time, where "what do we persist" is the actual question — not
        // mid-edit, where the user may still be changing their mind.
        if (value === 'character_block' && !updated.characterBlockConfig) {
          updated.characterBlockConfig = defaultCharacterBlockConfig(editingField.width);
        } else if (value === 'select' && !updated.selectConfig) {
          updated.selectConfig = defaultSelectConfig();
        } else if (value === 'image_region' && !updated.imageRegionConfig) {
          // NO DEFAULT PURPOSE. The three answers produce three different files and two of
          // them are told apart only by a human (Matterhorn 13-005 / 13-008), so a default
          // would be the tool answering an accessibility question on the author's behalf —
          // and it would then demand a description for a region they may have meant to
          // dismiss. The empty object exists only so the properties panel has something to
          // write into; validation blocks generation until the author chooses.
          updated.imageRegionConfig = {};
        } else if (value === 'paragraph' && !updated.paragraphConfig) {
          // NO DEFAULT ROLE. Whether a line is a heading, and at what level, is the author's
          // semantic call (Matterhorn Human) — the upload audit suggests, the author acts — so
          // a default would be the tool answering it for them. The empty object exists only so
          // the properties panel has something to write into; validation blocks generation
          // until the author picks a role and types the text.
          updated.paragraphConfig = {} as any;
        } else if (value === 'push_button' && !updated.pushButtonConfig) {
          // Clear-the-form is the one action that needs no further input, so it is
          // the default. The button's NAME is never defaulted: it is the Field
          // Label, and a push button with no label refuses to generate rather than
          // inventing one, because /MK /CA is the only name this control has.
          updated.pushButtonConfig = { action: 'reset' };
        }
      }
      setEditingField(updated);
      if (onFieldUpdate) {
        onFieldUpdate(updated);
      }
    };

    // Per-field validation across the whole working set. Edits apply live (no
    // Apply step), so invalid fields are surfaced in the list sidebar and BLOCK
    // session save until fixed — nothing invalid can persist.
    const invalidFields = useMemo(
      () =>
        selectedFields
          .map(field => ({ field, errors: getFieldValidationErrors(field) }))
          .filter(entry => entry.errors.length > 0),
      [selectedFields]
    );
    const invalidFieldIds = useMemo(() => new Set(invalidFields.map(entry => entry.field.id)), [invalidFields]);
    const invalidFieldsTooltip = invalidFields.length > 0
      ? `Fix these fields before saving:\n${invalidFields
          .map(entry => `• ${entry.field.name || entry.field.id}: ${entry.errors[0]}`)
          .join('\n')}`
      : undefined;

  // Render current page
  useEffect(() => {
    let renderTask: any = null;
    let isCancelled = false;
    
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current || !containerRef.current || isCancelled) return;

      try {
        // Cancel any existing render task
        if (renderTask) {
          renderTask.cancel();
          renderTask = null;
        }

        const page = await pdfDoc.getPage(currentPage);
        if (isCancelled) return;
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        if (!context || isCancelled) return;

        // Clear the canvas before rendering
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Use the user's selected scale
        const viewport = page.getViewport({ scale });
        
        // Set canvas dimensions to match the viewport
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // Set canvas display size to match actual size (no CSS scaling)
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.style.maxWidth = 'none';
        canvas.style.minWidth = 'none';
        canvas.style.maxHeight = 'none';
        canvas.style.minHeight = 'none';
        canvas.style.marginLeft = '0';
        canvas.style.marginRight = '0';

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          canvas: canvas
        };

        if (isCancelled) return;

        // Start the render task
        renderTask = page.render(renderContext);
        await renderTask.promise;
        
        if (!isCancelled) {
          console.log('Page rendered successfully at scale:', scale);
          
          // Only apply centering if user hasn't manually scrolled or if scale/page actually changed
          const shouldCenter = !userHasScrolled || scale !== lastScale || currentPage !== lastPage;

          if (shouldCenter) {
            setTimeout(() => {
              // Flexbox centres the wrapper via its auto margins; this only has to
              // place the scroll offset when the page overflows the padded viewport.
              centreCanvasScroll(containerRef.current, canvasRef.current);

              // Update tracking values
              setLastScale(scale);
              setLastPage(currentPage);

              // Reset user scroll flag if scale or page changed (user expects centering in these cases)
              if (scale !== lastScale || currentPage !== lastPage) {
                setUserHasScrolled(false);
              }
            }, 100); // Small delay to ensure DOM is updated
          }
        }
      } catch (err: any) {
        if (!isCancelled && err.name !== 'RenderingCancelledException') {
          console.error('Error rendering page:', err);
          setError('Failed to render PDF page');
        }
      }
    };

    renderPage();
    
    // Cleanup function to cancel ongoing render
    return () => {
      isCancelled = true;
      if (renderTask) {
        renderTask.cancel();
        renderTask = null;
      }
    };
  }, [pdfDoc, currentPage, scale, userHasScrolled, lastScale, lastPage]);

  // Cleanup scroll interval on unmount
  useEffect(() => {
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [scrollInterval]);

  // Add resize listener and scroll tracking
  useEffect(() => {
    const handleResize = () => {
      // Only adjust scroll during resize if user hasn't manually scrolled
      // Flexbox centering handles the positioning — only need to manage scroll for overflow
      if (!userHasScrolled && containerRef.current && canvasRef.current) {
        setTimeout(() => {
          centreCanvasScroll(containerRef.current, canvasRef.current);
        }, 100);
      }
    };

    const handleScroll = () => {
      // Mark that user has manually scrolled
      setUserHasScrolled(true);
    };

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    // Listen for scroll events to detect manual scrolling
    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', handleScroll);
    }

    // Listen for container resize using ResizeObserver if available
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', handleScroll);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [userHasScrolled]);

    // Load PDF document
    useEffect(() => {
      let isCancelled = false;
      
      const loadPDF = async () => {
        if (isCancelled) return;
        
        try {
          setIsLoading(true);
          setError(null);

          console.log('Loading PDF for document:', documentId);

          // Fetch PDF from backend with license key hash header
          const pdfUrl = getApiUrl(`/api/pdf/${documentId}/file`);
          console.log('PDF URL:', pdfUrl);

          if (isCancelled) return;

          // Fetch PDF as binary data with customer context header
          const pdfFetchHeaders: Record<string, string> = {};
          const pdfHash = getStoredLicenseKeyHash();
          if (pdfHash) {
            pdfFetchHeaders['X-License-Key-Hash'] = pdfHash;
          }
          const response = await fetch(pdfUrl, { credentials: 'include', headers: pdfFetchHeaders });
          console.log('PDF fetch response status:', response.status);

          if (!response.ok) {
            throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
          }

          if (isCancelled) return;

          const pdfData = await response.arrayBuffer();
          console.log('PDF data loaded, size:', pdfData.byteLength);

          if (isCancelled) return;

          console.log('Starting simple PDF.js getDocument...');

          // Load PDF from binary data (not URL) to support custom headers
          const loadingTask = pdfjsLib.getDocument({
            data: pdfData,
            useWorkerFetch: false,    // Disable worker fetching
            isEvalSupported: false,   // Disable eval for security
            useSystemFonts: false     // Use built-in fonts only
          });
          console.log('getDocument task created');
          
          // Add progress monitoring
          loadingTask.onProgress = (progress: any) => {
            if (!isCancelled) {
              console.log('PDF loading progress:', progress);
            }
          };
          
          console.log('Waiting for PDF promise...');
          const pdf = await loadingTask.promise;
          
          if (isCancelled) return;
          
          console.log('PDF promise resolved:', pdf);
          
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setCurrentPage(1);
          setIsLoading(false);

          console.log('PDF loaded successfully, pages:', pdf.numPages);

        } catch (err: any) {
          if (!isCancelled) {
            console.error('Error loading PDF:', err);
            console.error('Error details:', err.message, err.name, err.stack);
            setError(`Failed to load PDF: ${err.message || 'Unknown error'}`);
            setIsLoading(false);
          }
        }
      };

      if (documentId && !isCancelled) {
        loadPDF();
      } else {
        // No documentId — clear the displayed PDF
        if (pdfDoc) {
          pdfDoc.destroy();
        }
        setPdfDoc(null);
        setTotalPages(0);
        setIsLoading(false);
      }

      // Cleanup function to prevent race conditions
      return () => {
        isCancelled = true;
      };
    }, [documentId]);


    // Helper function to generate intelligent field name from nearby text
    const generateFieldName = (nearbyText: TextElement[]): string => {
      if (nearbyText.length === 0) return `field_${selectedFields.length + 1}`;
      
      // Find the most relevant text (usually labels are to the left or above)
      const relevantText = nearbyText
        .filter(el => el.text.trim().length > 1)
        .slice(0, 3)
        .map(el => el.text.trim())
        .join(' ')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 30);

      return relevantText || `field_${selectedFields.length + 1}`;
    };

    // Helper function to infer field type from nearby text
    const inferFieldType = (nearbyText: TextElement[]): 'text' | 'number' | 'date' | 'email' | 'checkbox' | 'select' => {
      const textContent = nearbyText.map(el => el.text.toLowerCase()).join(' ');
      
      if (textContent.includes('email') || textContent.includes('@')) return 'email';
      if (textContent.includes('date') || textContent.includes('birth') || textContent.includes('mm/dd')) return 'date';
      if (textContent.includes('phone') || textContent.includes('number') || textContent.includes('qty') || textContent.includes('amount')) return 'number';
      if (textContent.includes('check') || textContent.includes('agree') || textContent.includes('yes/no')) return 'checkbox';
      if (textContent.includes('select') || textContent.includes('choose') || textContent.includes('option')) return 'select';
      
      return 'text'; // Default to text
    };

    // Function to analyze text near coordinates and return intelligent field properties
    const analyzeTextNearCoordinate = async (x: number, y: number, fieldId: string): Promise<{name: string, type: 'text' | 'number' | 'date' | 'email' | 'checkbox' | 'select'}> => {
      try {
        console.log(`Analyzing text near coordinates (${x}, ${y}) for field ${fieldId}`);
        
        const response = await fetch(
          getApiUrl(`/api/pdf/${documentId}/text-near?x=${x}&y=${y}&radius=50.0`),
          { credentials: 'include' }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to analyze text: ${response.status}`);
        }
        
        const nearbyText: TextElement[] = await response.json();
        console.log(`Found ${nearbyText.length} text elements near field ${fieldId}:`, nearbyText);
        
        // Store analysis results for this field
        setFieldAnalysis(prev => ({
          ...prev,
          [fieldId]: nearbyText
        }));

        // Generate intelligent field properties
        const fieldName = generateFieldName(nearbyText);
        const fieldType = inferFieldType(nearbyText);

        return { name: fieldName, type: fieldType };
        
      } catch (error) {
        console.error('Error analyzing text near coordinates:', error);
        return { name: `field_${selectedFields.length + 1}`, type: 'text' };
      }
    };

    // View-mode panning: track the drag on document so it survives leaving the canvas
    useEffect(() => {
      if (!isPanning) return;
      const handlePanMove = (e: MouseEvent) => {
        const container = containerRef.current;
        const start = panStartRef.current;
        if (!container || !start) return;
        container.scrollLeft = start.scrollLeft - (e.clientX - start.x);
        container.scrollTop = start.scrollTop - (e.clientY - start.y);
      };
      const handlePanEnd = () => {
        panStartRef.current = null;
        setIsPanning(false);
      };
      document.addEventListener('mousemove', handlePanMove);
      document.addEventListener('mouseup', handlePanEnd);
      return () => {
        document.removeEventListener('mousemove', handlePanMove);
        document.removeEventListener('mouseup', handlePanEnd);
      };
    }, [isPanning]);

    // Space-hold = momentary pan mode. Escape has two jobs, in order - see below.
    useEffect(() => {
      const isTypingTarget = (target: EventTarget | null) => {
        const el = target as HTMLElement | null;
        if (!el || !el.tagName) return false;
        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable) {
          return true;
        }
        // Space ACTIVATES a focused button - it is a standard keyboard
        // activation key, alongside Enter. Without this, the handler's
        // preventDefault() below swallowed that: a keyboard user tabbing to a
        // button and pressing space got pan mode and the button never fired.
        // WCAG 2.1.1 Keyboard (Level A) - reachable by keyboard, not operable
        // by it. Invisible to anyone using a mouse, which is why it survived.
        //
        // closest() rather than a tagName check, so focus landing on a child
        // element inside a button is still recognised.
        return !!el.closest('button, [role="button"], a[href], summary');
      };
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code === 'Space' && !isTypingTarget(e.target)) {
          e.preventDefault(); // keep the page from scrolling while space pans
          setSpaceHeld(true);
        } else if (e.key === 'Escape') {
          /* ESC, IN THIS ORDER, AND THE ORDER IS THE SPECIFICATION:
             drag → drawer → selection → fullscreen, innermost first.

             THE DRAWER-CLOSE JOB CAME BACK WITH W3, and the rule that removed
             it decides that it should: Esc-to-dismiss is an obligation for a
             surface that COVERS content. The band covered nothing, so Esc
             retracting it was only a layout jump taxing the common case, and
             the job was removed. At drawer widths (~1024–1440px) the
             properties panel once again sits OVER the canvas edge, so what is
             under it has to be dismissable without a mouse — the same
             sentence, now pointing the other way. At three-column and tabbed
             widths the panel covers nothing and Esc correctly skips straight
             to the selection, exactly as the band-era rule reasoned.

             Closing the drawer is a DISMISSAL, not a saved preference — the
             owner's ruling, 6 Oct: "The ESC button should be a dismissal and
             cancel versus a save." So Esc goes through dismissPropsTransiently,
             which collapses the drawer WITHOUT writing the stored key, while
             the retract button keeps stating a remembered preference through
             togglePropsCollapsed. The first build of this chain routed Esc
             through the button's toggle for one-mechanism consistency, and
             that consistency had a cost: an Esc tonight retracted the rail for
             every session after, turning a flinch into a setting. Focus then returns to the
             selected field's row in the list: the drawer may have contained
             focus, and Esc must not strand the keyboard on a control that no
             longer exists. The row button is the edited field's one focusable
             representative outside the panel (data-field-row-id, D30's
             keyboard path), so the author lands exactly where their field is.

             1. A field drag in progress wins outright. It is the most
                transient state on screen and it is what Esc has always meant
                here.
             2. At drawer widths, an OPEN drawer closes, and the selection
                survives — the author dismissed a covering surface, not their
                place in the document.
             3. Otherwise the selection clears, which is what the panel's close
                button does.
             4. Otherwise fullscreen exits. Last on purpose: fullscreen is the
                OUTERMOST state on screen, and Esc has always meant "dismiss
                the innermost thing first". This is still ONE handler, now with
                four ordered jobs, rather than a second listener racing this
                one — two listeners deciding independently what Esc meant
                would both fire on one keypress.

             Esc NEVER discards an edit. Every change is already live through
             updateEditingField, so there is nothing to revert and a "cancel"
             reading would be a lie. */
          if (isSelecting) {
            if (scrollInterval) {
              clearInterval(scrollInterval);
              setScrollInterval(null);
            }
            setIsSelecting(false);
            setSelectionStart(null);
            setCurrentSelection(null);
          } else if (workspaceMode === 'drawer' && selectedFieldId && !propsCollapsed) {
            dismissPropsTransiently();
            // CSS.escape guards ids that are ours today but not guaranteed
            // selector-safe forever; the fallback keeps jsdom variants happy.
            const safeId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(selectedFieldId) : selectedFieldId;
            document.querySelector<HTMLElement>(`[data-field-row-id="${safeId}"]`)?.focus();
          } else if (selectedFieldId) {
            setSelectedFieldId(null);
          } else if (isFullscreen) {
            applyFullscreen(false);
          }
        }
      };
      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.code === 'Space') setSpaceHeld(false);
      };
      const handleWindowBlur = () => setSpaceHeld(false);
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      window.addEventListener('blur', handleWindowBlur);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        window.removeEventListener('blur', handleWindowBlur);
      };
      // dismissPropsTransiently is identity-stable (useCollapsiblePanel wraps
      // it in useCallback precisely so this list could carry it without
      // churning the listener every render).
    }, [isSelecting, scrollInterval, selectedFieldId, isFullscreen, applyFullscreen,
        workspaceMode, propsCollapsed, dismissPropsTransiently]);

    // Releasing the mouse outside the canvas left the drag stuck; cancel it there
    useEffect(() => {
      if (!isSelecting) return;
      const handleDocumentMouseUp = (e: MouseEvent) => {
        if (canvasRef.current && e.target instanceof Node && canvasRef.current.contains(e.target)) return;
        if (scrollInterval) {
          clearInterval(scrollInterval);
          setScrollInterval(null);
        }
        setIsSelecting(false);
        setSelectionStart(null);
        setCurrentSelection(null);
      };
      document.addEventListener('mouseup', handleDocumentMouseUp);
      return () => document.removeEventListener('mouseup', handleDocumentMouseUp);
    }, [isSelecting, scrollInterval]);

    // Ctrl+wheel / touchpad pinch zoom; React's onWheel is passive, so attach natively
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const handleWheel = (e: WheelEvent) => {
        if (!e.ctrlKey) return; // plain wheel keeps native scrolling
        e.preventDefault();
        wheelZoomAccumulatorRef.current += -e.deltaY * 0.002;
        if (Math.abs(wheelZoomAccumulatorRef.current) < 0.05) return;
        const delta = wheelZoomAccumulatorRef.current;
        wheelZoomAccumulatorRef.current = 0;
        setScale(prev => Math.min(3, Math.max(0.25, Math.round((prev + delta) * 20) / 20)));
      };
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }, [pdfDoc]);

    // Handle mouse down - start selection (Edit mode) or pan (View mode / space held)
    const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current) return;

      if (isViewMode) {
        const container = containerRef.current;
        if (!container) return;
        panStartRef.current = {
          x: event.clientX,
          y: event.clientY,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop
        };
        setIsPanning(true);
        return;
      }

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      
      // Debug coordinate calculation
      console.log('Canvas getBoundingClientRect():', rect);
      console.log('Canvas actual dimensions:', { width: canvas.width, height: canvas.height });
      console.log('Canvas style dimensions:', { width: canvas.style.width, height: canvas.style.height });
      console.log('Mouse event:', { clientX: event.clientX, clientY: event.clientY });
      
      // Calculate coordinates relative to the canvas
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      console.log('Calculated coordinates:', { x, y });

      setIsSelecting(true);
      setSelectionStart({ x, y });
      setCurrentSelection({
        x,
        y,
        width: 0,
        height: 0,
        pageNumber: currentPage,
        id: '', // Will be set when finalized
        name: '', // Will be set when finalized
        type: 'text' // Default type
      });

      console.log('Selection started at:', { x, y });
    };

    // Handle mouse move - update selection
    const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isSelecting || !selectionStart || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      
      // Calculate coordinates relative to the canvas
      // Since canvas size now matches display size, coordinates map directly
      const currentX = event.clientX - rect.left;
      const currentY = event.clientY - rect.top;

      // Auto-scroll functionality
      const scrollContainer = canvas.parentElement;
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollThreshold = 50; // pixels from edge to trigger scroll
        const scrollSpeed = 8; // pixels per scroll step
        
        let shouldScroll = false;
        let scrollDirection = { x: 0, y: 0 };
        
        // Determine scroll direction based on mouse position
        if (event.clientY < containerRect.top + scrollThreshold) {
          scrollDirection.y = -1; // scroll up
          shouldScroll = true;
        } else if (event.clientY > containerRect.bottom - scrollThreshold) {
          scrollDirection.y = 1; // scroll down
          shouldScroll = true;
        }
        
        if (event.clientX < containerRect.left + scrollThreshold) {
          scrollDirection.x = -1; // scroll left
          shouldScroll = true;
        } else if (event.clientX > containerRect.right - scrollThreshold) {
          scrollDirection.x = 1; // scroll right
          shouldScroll = true;
        }
        
        // Clear existing scroll interval
        if (scrollInterval) {
          clearInterval(scrollInterval);
          setScrollInterval(null);
        }
        
        // Start new scroll interval if needed
        if (shouldScroll) {
          const interval = setInterval(() => {
            if (scrollDirection.y !== 0) {
              if (scrollDirection.y < 0) {
                // Scroll up
                scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop - scrollSpeed);
              } else {
                // Scroll down
                scrollContainer.scrollTop = Math.min(
                  scrollContainer.scrollHeight - scrollContainer.clientHeight,
                  scrollContainer.scrollTop + scrollSpeed
                );
              }
            }
            
            if (scrollDirection.x !== 0) {
              if (scrollDirection.x < 0) {
                // Scroll left
                scrollContainer.scrollLeft = Math.max(0, scrollContainer.scrollLeft - scrollSpeed);
              } else {
                // Scroll right
                scrollContainer.scrollLeft = Math.min(
                  scrollContainer.scrollWidth - scrollContainer.clientWidth,
                  scrollContainer.scrollLeft + scrollSpeed
                );
              }
            }
          }, 50); // Smooth scrolling every 50ms
          
          setScrollInterval(interval);
        }
      }

      // Calculate selection bounds (handle both directions)
      const minX = Math.min(selectionStart.x, currentX);
      const minY = Math.min(selectionStart.y, currentY);
      const maxX = Math.max(selectionStart.x, currentX);
      const maxY = Math.max(selectionStart.y, currentY);

      setCurrentSelection(prev => prev ? {
        ...prev,
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      } : null);
    };

    // Handle mouse up - finalize selection
    const handleMouseUp = async (event: React.MouseEvent<HTMLCanvasElement>) => {
      // Clear any active scroll interval
      if (scrollInterval) {
        clearInterval(scrollInterval);
        setScrollInterval(null);
      }
      
      if (!isSelecting || !currentSelection || currentSelection.x === undefined || currentSelection.y === undefined) return;

      // Ignore bare clicks and sub-threshold jitters — accidental clicks were creating fields
      const MIN_DRAG_PX = 8;
      const dragWidth = currentSelection.width || 0;
      const dragHeight = currentSelection.height || 0;
      if (dragWidth < MIN_DRAG_PX && dragHeight < MIN_DRAG_PX) {
        setIsSelecting(false);
        setSelectionStart(null);
        setCurrentSelection(null);
        return;
      }

      // A drag that is thin in one direction still falls back to the default field size
      const isDrag = dragWidth > 5 && dragHeight > 5;

      // Generate unique field ID
      const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      let finalField: FieldCoordinates;
      let analysisX: number, analysisY: number;

      if (isDrag) {
        // Drag selection - use the actual selection area (normalize to 100% zoom)
        finalField = {
          x: currentSelection.x / scale,
          y: currentSelection.y / scale,
          width: (currentSelection.width || 0) / scale,
          height: (currentSelection.height || 0) / scale,
          pageNumber: currentSelection.pageNumber || currentPage,
          id: fieldId,
          name: `new_field_${selectedFields.length + 1}`, // Will be updated after analysis
          type: 'text',
          required: false
        };

        // Analyze text near the center of the selection (use display coordinates for analysis)
        analysisX = currentSelection.x + (currentSelection.width || 0) / 2;
        analysisY = currentSelection.y + (currentSelection.height || 0) / 2;

        console.log('Drag field created:', finalField);
      } else {
        // Single click - create default-sized field at click point
        // Click selection - use click point with default size (normalize to 100% zoom)
        finalField = {
          x: currentSelection.x / scale,
          y: currentSelection.y / scale,
          width: 100 / scale, // Default width for single click (normalized)
          height: 20 / scale, // Default height for single click (normalized) 
          pageNumber: currentPage,
          id: fieldId,
          name: `new_field_${selectedFields.length + 1}`, // Will be updated after analysis
          type: 'text',
          required: false
        };

        // Use display coordinates for analysis (currentSelection coordinates are in display scale)
        analysisX = currentSelection.x;
        analysisY = currentSelection.y;

        console.log('Click field created:', finalField);
      }

      // Send field to parent component immediately
      if (onFieldSelect) {
        onFieldSelect(finalField);
      }
      setSelectedFieldId(fieldId);

      // New fields are configured in the properties sidebar (the floating
      // dialog is retired) — focus the name input so typing starts immediately.
      // Small delay lets the sidebar mount for the new selection first.
      setTimeout(() => {
        const nameInput = document.getElementById(FIELD_PROPS_NAME_INPUT_ID) as HTMLInputElement | null;
        if (nameInput) {
          nameInput.focus();
          nameInput.select();
        }
      }, 350);

      // Analyze text and update field properties
      try {
        const { name, type } = await analyzeTextNearCoordinate(analysisX, analysisY, fieldId);
        
        // Update the field with intelligent properties via parent callback
        const existingField = selectedFields.find(f => f.id === fieldId);
        if (existingField && onFieldUpdate) {
          onFieldUpdate({ ...existingField, name, type });
        }

        console.log(`Field ${fieldId} updated with name: ${name}, type: ${type}`);
      } catch (error) {
        console.error('Failed to analyze field:', error);
        // Update with fallback name via parent callback
        const existingField = selectedFields.find(f => f.id === fieldId);
        if (existingField && onFieldUpdate) {
          onFieldUpdate({ ...existingField, name: `field_${selectedFields.length}` });
        }
      }

      // Reset selection state
      setIsSelecting(false);
      setSelectionStart(null);
      setCurrentSelection(null);
    };

    // Function to handle save session (with optional confirmation)
    const handleSaveSession = () => {
      if (ENABLE_SAVE_CONFIRMATION) {
        setShowSaveConfirmation(true);
      } else {
        saveEditingSession();
      }
    };

    // Function to fetch available sessions
    const fetchAvailableSessions = async () => {
      setIsLoadingSessions(true);
      try {
        const listHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        const listHash = getStoredLicenseKeyHash();
        if (listHash) {
          listHeaders['X-License-Key-Hash'] = listHash;
        }
        const response = await fetch(getApiUrl('/api/pdf/sessions'), {
          method: 'GET',
          credentials: 'include',
          headers: listHeaders,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch sessions: ${response.status}`);
        }

        const sessions = await response.json();
        // Sort sessions by most recent first (updatedAt descending)
        const sortedSessions = sessions.sort((a: any, b: any) => {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });
        setAvailableSessions(sortedSessions);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        setErrorMessage('Failed to load available sessions. Please try again.');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 6000);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    // Function to open load session dialog
    const openLoadDialog = () => {
      setShowLoadDialog(true);
      fetchAvailableSessions();
    };

    // Helper function to format timestamps with relative time
    const formatTimestamp = (timestamp: string) => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      let relative = '';
      if (diffMins < 1) {
        relative = 'Just now';
      } else if (diffMins < 60) {
        relative = `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        relative = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        relative = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      } else {
        relative = date.toLocaleDateString();
      }

      return {
        relative,
        absolute: date.toLocaleString(),
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
    };

    // Helper function to get deployment status styling
    const getDeploymentStatusStyle = (status: string) => {
      switch (status) {
        case 'planning':
          return 'bg-gray-100 text-gray-800';
        case 'development':
          return 'bg-blue-100 text-blue-800';
        case 'testing':
          return 'bg-yellow-100 text-yellow-800';
        case 'release':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-600';
      }
    };

    // Helper function to get deployment status icon
    const getDeploymentStatusIcon = (status: string) => {
      switch (status) {
        case 'planning':
          return (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          );
        case 'development':
          return (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          );
        case 'testing':
          return (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        case 'release':
          return (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          );
        default:
          return null;
      }
    };

    // Function to save editing session
    const saveEditingSession = async () => {
      if (!sessionName.trim()) {
        setErrorMessage('Please enter a session name');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 4000);
        return;
      }

      if (isEditingExistingSession && !changeComment.trim()) {
        setErrorMessage('Please add a comment describing your changes');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 4000);
        return;
      }

      setIsSavingSession(true);
      
      try {
        const sessionData = {
          sessionId: currentSessionId, // Include existing session ID if available
          sessionName: sessionName.trim(),
          documentId,
          originalFilename,
          selectedFields,
          currentPage,
          scale,
          // DOC-03. The author's document title and language ride on the session
          // row now; without them here the main Save path would keep discarding
          // the two properties only a human can supply.
          documentTitle: documentTitle.trim(),
          documentLanguage: documentLanguage.trim(),
          // The reading-order decision rides every save (0d) — this is the main
          // Save path, so omitting it here is how Manual reverted to AUTO on
          // reload. THE MODE, never the view sort (981b8e3).
          readingOrderMode: readingOrderMode,
          deploymentStatus,
          changeComment: changeComment.trim(), // Include change comment
          description: sessionDescription.trim(), // Include session description
          createdByUserEmail: authState.user?.email || authState.user?.displayName || null // Signed-in user (email; displayName holds it when email is null)
        };

        console.log('Saving editing session:', sessionData);

        // Save to backend
        const saveHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        const saveHash = getStoredLicenseKeyHash();
        if (saveHash) {
          saveHeaders['X-License-Key-Hash'] = saveHash;
        }
        const saveEmail = getStoredUserEmail();
        if (saveEmail) {
          saveHeaders['X-User-Email'] = saveEmail;
        }
        const response = await fetch(
          getApiUrl('/api/pdf/sessions/save'),
          {
            method: 'POST',
            credentials: 'include',
            headers: saveHeaders,
            body: JSON.stringify(sessionData),
          }
        );

        if (!response.ok) {
          // A capacity refusal is not an error. It has its own prompt with a way
          // forward, so it must not also surface as a red failure notification.
          const refusal = readSessionRefusal(response.status, response.headers);
          if (refusal) {
            if (refusal.kind === 'limit') {
              showLimit(refusal.message);
            } else {
              showGate('sessions');
            }
            throw new SessionRefusalShown(refusal.message);
          }

          const errorText = await response.text();
          let errorMessage = `Failed to save session: ${response.status}`;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorMessage;
          } catch (e) {
            errorMessage += ` - ${errorText}`;
          }
          throw new Error(errorMessage);
        }

        const savedSession = await response.json();
        console.log('Session saved successfully:', savedSession);

        // Notify parent of the saved session ID so it can update session context
        if (onSessionSaved && savedSession.sessionId) {
          onSessionSaved(savedSession.sessionId, savedSession.sessionName || sessionName.trim());
        }

        // Clear unsaved changes flag in parent after successful save
        if (onUnsavedChangesUpdate) {
          console.log('PDFViewer: Clearing hasUnsavedChanges after successful session save');
          onUnsavedChangesUpdate(false);
        }
        
        // Show custom success notification
        const message = isEditingExistingSession 
          ? `Session "${sessionName}" updated successfully!`
          : `Session "${sessionName}" saved successfully!`;
        setSuccessMessage(message);
        setShowSuccessNotification(true);
        setShowSaveDialog(false);
        setChangeComment('');
        setSessionDescription(''); // Always clear description after save

        // For new sessions, also clear session name and reset deployment status
        if (!isEditingExistingSession) {
          setSessionName('');
          setDeploymentStatus('planning');
        }
        
        // Auto-hide notification after 4 seconds
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 4000);
        
      } catch (error) {
        // Already shown, with its own prompt and buttons. Stacking a red
        // "Failed to save session" on top would contradict it.
        if (error instanceof SessionRefusalShown) {
          setIsSavingSession(false);
          return;
        }
        console.error('Error saving session:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
        
        // Show custom error notification
        setErrorMessage(`Failed to save session: ${errorMsg}`);
        setShowErrorNotification(true);
        
        // Auto-hide notification after 6 seconds
        setTimeout(() => {
          setShowErrorNotification(false);
        }, 6000);
      } finally {
        setIsSavingSession(false);
      }
    };

    // Handler for load session that checks unsaved changes
    const handleLoadSession = async (sessionId: string) => {
      console.log('PDFViewer: handleLoadSession called, delegating to parent', { sessionId, hasUnsavedChanges });
      
      // Always delegate to parent's session loading function which has comprehensive comparison logic
      if (onLoadSession) {
        console.log('PDFViewer: Delegating to parent session loading');
        onLoadSession(sessionId);
        setShowLoadDialog(false); // Close the load dialog
      } else {
        console.warn('PDFViewer: onLoadSession prop not provided, falling back to local loading');
        // Fallback to local loading if parent doesn't provide handler
        await loadEditingSession(sessionId);
      }
    };

    // Function to load editing session
    const loadEditingSession = async (sessionId: string) => {
      try {
        const loadHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        const loadHash = getStoredLicenseKeyHash();
        if (loadHash) {
          loadHeaders['X-License-Key-Hash'] = loadHash;
        }
        const response = await fetch(
          getApiUrl(`/api/pdf/sessions/${sessionId}`),
          {
            method: 'GET',
            credentials: 'include',
            headers: loadHeaders,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load session: ${response.status}`);
        }

        const sessionData = await response.json();
        console.log('Session loaded successfully:', sessionData);
        
        // Clear existing fields first (since PDFViewer doesn't directly manage fields state)
        // Note: In a more complete implementation, this would clear parent state
        
        // Apply session data - restore fields by calling onFieldSelect for each
        if (sessionData.selectedFields && onFieldSelect) {
          sessionData.selectedFields.forEach((field: FieldCoordinates) => {
            onFieldSelect(field);
          });
        }
        
        // Restore view state with proper validation
        setCurrentPage((sessionData.currentPage && sessionData.currentPage > 0) ? sessionData.currentPage : 1);
        setScale((sessionData.scale && sessionData.scale > 0) ? sessionData.scale : 0.75);

        // Set session metadata (session ID is managed by parent)
        setSessionName(sessionData.sessionName);
        setDeploymentStatus(sessionData.deploymentStatus || 'planning');
        setIsEditingExistingSession(true);
        // Clear unsaved changes flag in parent
        if (onUnsavedChangesUpdate) {
          onUnsavedChangesUpdate(false);
        }
        
        // Close dialog and show success
        setShowLoadDialog(false);
        setSelectedSessionToLoad(null);
        setSuccessMessage(`Session "${sessionData.sessionName}" loaded successfully! Restored ${sessionData.selectedFields?.length || 0} fields.`);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);
        
      } catch (error) {
        console.error('Error loading session:', error);
        setErrorMessage('Failed to load session. Please try again.');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 6000);
      }
    };

    // Unsaved changes dialog handlers
    const handleDiscardChanges = async () => {
      setShowUnsavedChangesDialog(false);
      // Clear unsaved changes flag in parent
      if (onUnsavedChangesUpdate) {
        onUnsavedChangesUpdate(false);
      }
      if (pendingSessionId) {
        // Use parent's session loading to properly replace fields
        if (onLoadSession) {
          onLoadSession(pendingSessionId);
        } else {
          await loadEditingSession(pendingSessionId);
        }
        setPendingSessionId(null);
      }
    };

    const handleSaveAndContinue = async () => {
      // Save current session if we have one
      if (currentSessionId) {
        try {
          await saveCurrentSession();
          setShowUnsavedChangesDialog(false);
          if (pendingSessionId) {
            // Use parent's session loading to properly replace fields
            if (onLoadSession) {
              onLoadSession(pendingSessionId);
            } else {
              await loadEditingSession(pendingSessionId);
            }
            setPendingSessionId(null);
          }
        } catch (error) {
          console.error('Error saving session:', error);
          setErrorMessage('Failed to save changes. Please try again.');
          setShowErrorNotification(true);
          setTimeout(() => setShowErrorNotification(false), 6000);
        }
      } else {
        // No current session to save, just discard changes
        await handleDiscardChanges();
      }
    };

    const saveCurrentSession = async () => {
      if (!currentSessionId) {
        throw new Error('No current session to save');
      }

      const sessionData = {
        sessionId: currentSessionId,
        // Required by the save endpoint. Nothing has been typed into the save dialog
        // on an auto-created session, so fall back to what the upload was called.
        sessionName: sessionName.trim() || projectName || originalFilename,
        documentId,
        originalFilename,
        selectedFields,
        currentPage,
        scale,
        // DOC-03. Same two properties on the auto-save path.
        documentTitle: documentTitle.trim(),
        documentLanguage: documentLanguage.trim(),
        // And the reading-order decision (0d), as on every other save path.
        readingOrderMode: readingOrderMode,
        deploymentStatus: deploymentStatus || 'development',
        changeComment: 'Saved pending field changes before loading another session',
        createdByUserEmail: authState.user?.email || authState.user?.displayName || null // Signed-in user (email; displayName holds it when email is null)
      };

      console.log('Auto-saving current session:', sessionData);

      const autoSaveHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      const autoSaveHash = getStoredLicenseKeyHash();
      if (autoSaveHash) {
        autoSaveHeaders['X-License-Key-Hash'] = autoSaveHash;
      }
      const autoSaveEmail = getStoredUserEmail();
      if (autoSaveEmail) {
        autoSaveHeaders['X-User-Email'] = autoSaveEmail;
      }
      const response = await fetch(getApiUrl('/api/pdf/sessions/save'), {
        method: 'POST',
        credentials: 'include',
        headers: autoSaveHeaders,
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        // A capacity refusal is not an error. It has its own prompt with a way
        // forward, so it must not also surface as a red failure notification.
        const refusal = readSessionRefusal(response.status, response.headers);
        if (refusal) {
          if (refusal.kind === 'limit') {
            showLimit(refusal.message);
          } else {
            showGate('sessions');
          }
          throw new SessionRefusalShown(refusal.message);
        }

        const errorText = await response.text();
        let errorMessage = `Failed to save session: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          errorMessage += ` - ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const savedSession = await response.json();
      console.log('Session auto-saved successfully:', savedSession);
      return savedSession;
    };

    const handleCancelLoadSession = () => {
      setShowUnsavedChangesDialog(false);
      setPendingSessionId(null);
    };

    // Function to generate form configuration
    const generateFormConfiguration = async () => {
      if (selectedFields.length === 0) {
        setErrorMessage('Please select at least one field before generating the Spring Boot project.');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 4000);
        return;
      }

      // Check if session is saved
      if (!currentSessionId) {
        setErrorMessage('Please save the session before generating a Spring Boot project.');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 4000);
        return;
      }

      // Check for unsaved changes
      if (hasUnsavedChanges) {
        setErrorMessage('Please save your changes before generating a Spring Boot project.');
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 4000);
        return;
      }

      setIsGeneratingForm(true);

      try {
        console.log('Generating Spring Boot project from session:', currentSessionId);

        // Prepare the request payload for code generation
        const formRequest = {
          sessionId: currentSessionId,
          formName: originalFilename ? originalFilename.replace(/\.[^/.]+$/, "") : "Generated Form"
        };

        console.log('Spring Boot project request payload:', formRequest);

        // Call the code generation endpoint
        const response = await fetch(
          getApiUrl(`/api/pdf/forms/${documentId}/generate-code`),
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formRequest),
          }
        );

        if (!response.ok) {
          // Try to extract error message from response
          const errorHeader = response.headers.get('X-Error-Message');
          let errorMessage = 'Failed to generate Spring Boot project';

          if (errorHeader) {
            errorMessage = errorHeader;
          } else {
            try {
              const errorText = await response.text();
              if (errorText) {
                errorMessage = errorText;
              }
            } catch (e) {
              errorMessage = `Failed to generate Spring Boot project: ${response.status}`;
            }
          }

          setErrorMessage(errorMessage);
          setShowErrorNotification(true);
          setTimeout(() => setShowErrorNotification(false), 6000);
          setIsGeneratingForm(false);
          return;
        }

        // Handle ZIP file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${formRequest.formName}_spring_boot_project.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        setSuccessMessage(`Spring Boot project "${formRequest.formName}" generated and downloaded successfully!`);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);

      } catch (error) {
        console.error('Error generating Spring Boot project:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate Spring Boot project. Please try again.';
        setErrorMessage(errorMessage);
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 6000);
      } finally {
        setIsGeneratingForm(false);
      }
    };

    /**
     * The properties panel, built ONCE for the three slots that can hold it —
     * the rail cell, the drawer, and the Properties tab. One mode is active
     * at a time, so exactly one instance renders; a helper rather than three
     * inline copies because the prop list is the panel's contract and three
     * drifting copies of a contract is how the dblclick handlers' bug class
     * gets written. Only `twoColumn` varies by slot: the drawer and the tab
     * are never wide enough to earn it, the rail says so from its width.
     */
    const renderPropertiesPanel = (twoColumn: boolean) =>
      editingField ? (
        <FieldPropertiesSidebar
          field={editingField}
          updateField={updateEditingField}
          validationErrors={getFieldValidationErrors(editingField)}
          allFields={selectedFields}
          collapsed={propsCollapsed}
          onToggleCollapsed={togglePropsCollapsed}
          twoColumn={twoColumn}
          onDelete={() => {
            if (onFieldDelete) {
              onFieldDelete(editingField.id);
            }
            setSelectedFieldId(null);
          }}
          onClose={() => setSelectedFieldId(null)}
        />
      ) : null;

    /**
     * Arrow keys walk the tabbed rail's two tabs, per the ARIA tabs pattern:
     * roving tabindex, focus follows selection. With exactly two tabs both
     * arrows toggle (which is also what wrapping produces); Home and End name
     * the ends. Delegated from the tablist so there is one handler, not one
     * per tab.
     */
    const handleTabListKeyDown = (event: React.KeyboardEvent) => {
      let next: 'fields' | 'properties' | null = null;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        next = activeTab === 'fields' ? 'properties' : 'fields';
      } else if (event.key === 'Home') {
        next = 'fields';
      } else if (event.key === 'End') {
        next = 'properties';
      }
      if (next !== null) {
        event.preventDefault();
        setActiveTab(next);
        document.getElementById(next === 'fields' ? EDITOR_TAB_FIELDS_ID : EDITOR_TAB_PROPS_ID)?.focus();
      }
    };

    // ONE SCROLL REGION FOR THE PAGE, AND THE DOCUMENT SCROLLS ABOVE IT.
    //
    // The root used to be `min-h-screen` with the canvas container pinned to
    // `minHeight: 900px` / `maxHeight: 90vh`. Those two fought each other: on a
    // 768px-tall laptop 90vh is ~690px, BELOW the 900px floor, so the floor won and the
    // inner box was taller than the window. The author then scrolled the PAGE to reach
    // the bottom of a box that had its own scrollbar - two nested scrollers for one
    // document, which is why this felt broken on a laptop and merely odd on a monitor.
    //
    // 00cadac replaced that with `calc(100vh - 7rem)`, and 66ef98d repaired the flex
    // chain underneath it so the height actually reached the scroller. Both were right
    // about the card. The pass after them claimed the DOCUMENT no longer scrolled
    // either, and that claim was false: the app's hero block alone is taller than a
    // 720px window, so the page above this card has always scrolled and no height
    // given to this card can change it. The claim came from a fixture whose page
    // chrome was one row tall - see cardHeight for the full account, because the same
    // fixture is what produced the height regression QA found.
    //
    // What IS true, and is the thing worth protecting: once the author has scrolled
    // to the editor, the card fills the window and THE PAGE IS THE ONLY THING THAT
    // SCROLLS INSIDE IT.
    //
    // `min-h-0` on each flex child is the part that is easy to miss: without it a flex
    // child refuses to shrink below its content and the inner scrollbar never engages -
    // the same bug wearing a different hat. So is a missing `flex-1`. Both have shipped
    // from this file, one level apart, within two days of each other.
    return (
      <div
        /* FULLSCREEN SWAPS THE CARD'S GEOMETRY, NOTHING ELSE. fixed inset-0
           supplies the height, so the measured cardHeight style comes off
           rather than fighting it — two height authorities over one box is the
           exact two-floors defect this card has shipped twice.

           z-50, not something higher, and the number is load-bearing twice
           over: the site header is sticky z-50 and EARLIER in the DOM, so the
           card paints over it on DOM order without outranking anything else;
           and the dialogs this card owns (save session, load session, the
           notifications) are fixed z-50 INSIDE the stacking context this fixed
           element creates, so they stack above the fullscreen card exactly as
           they stack above the normal one. The Getting Started guide at z-[60]
           stays above by design — it floats over dialog backdrops everywhere. */
        className={`bg-white p-2 max-w-none flex flex-col ${
          isFullscreen ? 'fixed inset-0 z-50' : 'rounded-lg shadow-md w-full'
        }`}
        style={isFullscreen ? undefined : { height: cardHeight }}
        /* MORE CANVAS, NOT MORE CHROME. Scott, 17 Sep: "Adding another 200px to the
           height of the PDF edit area would be good"; 18 Sep: "add at least 200px or
           more to make the PDF view area much much larger"; and then, from QA on the
           build that was supposed to deliver it: "The PDF View layout height is
           shorter, not 200px larger."

           Reclaimed WITHOUT touching the scroll chain, over three rounds. 17 Sep:
           p-8 -> p-4 (64px), the title row folded into one line (~70px), the toolbar
           margin mb-4 -> mb-2 (8px). 18 Sep: p-4 -> p-2 (16px), the title row margin
           mb-2 -> mb-1 (4px), and the SESSION MANAGEMENT CARD deleted outright
           (~100px) - its three buttons moved into the sticky toolbar, where the
           unsaved-changes badge is now visible at ALL times instead of scrolling away
           with a gradient card.

           THE THIRD ROUND IS THE ONE THAT MATTERED, and it was not a subtraction: the
           card's height was being computed from its own position in a document whose
           chrome is 600-820px tall, so it was resolving to its floor. See cardHeight
           above. That single arithmetic error was costing more height than every
           saving in this list put together - ~160px at 1280x720 and ~780px at
           2560x1440.

           There is ONE floor now and it lives in cardHeight (MIN_CARD_PX). The 32rem
           CSS minHeight that used to sit here was a second floor, and it is what
           actually won when the measurement collapsed - the classic two-constraints
           defect this card has now shipped twice. */
      >
        {/* Header. ONE LINE, NOT THREE. The title and document id used to occupy a
            70px block above a toolbar that was already there; on a laptop that is a
            quarter of the canvas spent restating what the tab and the page already
            show. The id stays - it is what someone quotes in a support request - but
            as secondary text on the same row, and truncated so a long filename cannot
            push the row taller. */}
        <div className="mb-1 flex items-baseline gap-3 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            PDF Viewer - {projectName || originalFilename?.replace(/\.pdf\.pdf$/i, '.pdf')}
          </h3>
          <p className="text-xs text-gray-500 whitespace-nowrap">
            Document ID: {documentId}
          </p>
        </div>

        {/* Selection is announced here, politely, instead of by stealing focus. */}
        <div aria-live="polite" className="sr-only">{selectionAnnouncement}</div>

        {/* The fullscreen change is announced in its OWN polite region, not the
            selection one: that region's text is derived during render as a pure
            function of the current selection, and pushing a mode change through
            it would mean corrupting that derivation with state it was built to
            avoid. Two regions, each a pure voice for one fact. */}
        <div aria-live="polite" className="sr-only">{fullscreenAnnouncement}</div>

        {/* The drawer's open/close, the THIRD pure voice — see its state above
            for why it neither shares the selection region nor speaks outside
            drawer widths. */}
        <div aria-live="polite" className="sr-only">{drawerAnnouncement}</div>

        {/* Main Layout: the editor column, the full width of the card.

            The surfaces have travelled: an in-flow left rail until 17 Sep, an
            overlay until QA on 18 Sep, a band above the page until W3 — and
            now three surfaces sharing the work-area row, list | canvas |
            properties. See the header note for the full history and for why
            each prior shape lost.

            flex-1 min-h-0 CARRIES THE CARD HEIGHT DOWN TO THE SCROLLER.
            Without these two, the card's measured height stops here: the scroll
            container below gets no height cap, its overflow-auto never engages, and
            a tall or zoomed page overflows a fixed-height root sitting inside an
            overflow-hidden ancestor (PDFUpload.tsx) - clipped and unreachable,
            which is WORSE than the two nested scrollbars this replaced.
            items-start is GONE with the rail it existed for: the single child must
            now stretch, and it also opts in itself with self-stretch. flex-1 on a
            child of a non-flex parent is silently inert, which is how this shipped
            unnoticed - every link in this chain is load-bearing. */}
        <div className="flex w-full flex-1 min-h-0">
        <div className="flex flex-col gap-8 flex-1 min-w-0">

          {/* PDF Viewer Section - Full Width */}
          {/* AND THE CHAIN WAS STILL BROKEN HERE, one link below where 66ef98d
              repaired it. This div had min-h-0 and self-stretch but NO flex-1, and
              self-stretch in a COLUMN flex container is a cross-axis property - it
              stretches width, not height. So this box took its CONTENT height and
              the root's calc(100vh - …) stopped one level short of the scroller.

              Measured, headless, against the real compiled stylesheet: at 2560x1440
              the canvas came out 792px - the height of one US Letter page at 100% -
              inside a 1392px card, leaving ~600px of the window unused and unusable.
              It was invisible at 1280x720 only because the content happened to
              exceed the space there, so the clamp did the job flex-1 should have.

              This is the same defect 00cadac shipped and 66ef98d fixed, one level
              deeper: EVERY link in this chain is load-bearing, and a missing flex-1
              on a flex child is silent. */}
          <div className="w-full flex flex-col min-h-0 flex-1 self-stretch">

        {/* PDF Controls - slim sticky icon toolbar (tooltips replace group labels).
            flex-shrink-0: it is a column flex child now competing with a work area
            that has a 500px floor, and a squeezed toolbar is a clipped toolbar. */}
        <div className="mb-2 w-full flex-shrink-0 sticky top-2 z-20 flex items-center gap-1 px-2 py-1.5 bg-white/95 backdrop-blur rounded-lg border border-gray-200 shadow-sm overflow-x-auto">

          {/* Page navigation */}
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            title="Previous page"
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-sm font-semibold text-gray-800 min-w-[64px] text-center tabular-nums" title="Current page">
            {currentPage} / {totalPages || '?'}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            title="Next page"
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2 flex-shrink-0" />

          {/* Mouse mode: segmented Edit / Pan toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setInteractionMode('edit')}
              title="Edit mode — drag on the PDF to create a field. Tip: hold Space to pan the view without leaving Edit"
              className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
                !isViewMode ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => setInteractionMode('view')}
              title="Pan mode — drag to move around the PDF, no fields created. Tip: hold Space while editing to pan temporarily"
              className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
                isViewMode ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
              </svg>
              Pan
            </button>
          </div>

          <div className="w-px h-6 bg-gray-200 mx-2 flex-shrink-0" />

          {/* Field-name overlay toggle */}
          <button
            onClick={() => setShowFieldNames(!showFieldNames)}
            title={showFieldNames ? 'Field names shown on the PDF — click to hide' : 'Field names hidden — click to show them on the PDF'}
            className={`p-2 rounded-md flex items-center gap-1.5 text-sm font-medium transition-colors ${
              showFieldNames ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {showFieldNames ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            )}
            Names
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2 flex-shrink-0" />

          {/* Zoom */}
          <button
            onClick={() => setScale(Math.max(0.25, scale - 0.25))}
            disabled={scale <= 0.25}
            title="Zoom out"
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <div className="text-sm font-semibold text-gray-800 min-w-[48px] text-center tabular-nums" title="Zoom level">
            {Math.round(scale * 100)}%
          </div>
          <button
            onClick={() => setScale(Math.min(3, scale + 0.25))}
            disabled={scale >= 3}
            title="Zoom in"
            className="p-2 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={handleFitWidth}
            disabled={!pdfDoc}
            title="Fit the page to the width free beside the field list. Nothing ever rescales on its own — open or retract a panel and the page stays exactly where it is; press this when you want it refitted."
            className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6v12M20 6v12M8 9l-3 3 3 3m8-6l3 3-3 3M5 12h14" />
            </svg>
            Fit width
          </button>
          {/* FIT PAGE - the answer to "should there be a setting that makes the
              vertical view extremely tall". No: a stored height is a decision the
              author has to keep making and can get wrong, and "show me the whole
              page" is a ZOOM operation. Fit Page is the standard control for it -
              one press, no state, correct at any window or page size. The reasoning
              is at handleFitPage; it is written down so the setting is not
              re-proposed. */}
          <button
            onClick={handleFitPage}
            disabled={!pdfDoc}
            title="Fit the whole page — both dimensions — into the free space. One press; nothing is stored and nothing rescales on its own."
            className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V5a1 1 0 011-1h3M4 16v3a1 1 0 001 1h3m8-16h3a1 1 0 011 1v3m0 8v3a1 1 0 01-1 1h-3" />
            </svg>
            Fit page
          </button>
          {/* FULLSCREEN TOGGLE. The label is the state machine: it always names
              the ACTION the press will take, so the accessible name never lies
              about which way the toggle is facing — the swapped-label pattern,
              chosen over aria-pressed because a pressed-state reading of
              "Full screen: pressed" is more puzzle than announcement. */}
          <button
            onClick={() => applyFullscreen(!isFullscreen)}
            title={isFullscreen
              ? 'Return the editor to the page. Esc leaves full screen too, once nothing else is in progress.'
              : 'Spread the editor over the whole window. Leave with this button or Esc.'}
            className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            {isFullscreen ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0v4m0-4h4m7 5l5-5m0 0v4m0-4h-4M9 15l-5 5m0 0v-4m0 4h4m7-5l5 5m0 0v-4m0 4h-4" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 1v4m0 0h-4m4 0l-5-5" />
              </svg>
            )}
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
          {/* SESSION ACTIONS - the document-level ones, moved here out of a 100px
              gradient card that sat between the title and the toolbar.

              The card was decoration wrapped around three buttons: an icon tile, a
              heading that said "Session Management", and a line of body text that said
              what the buttons already say. That is ~100px of the scarce dimension spent
              restating the controls, on every screen, permanently - and it is the single
              biggest reclaim available above the canvas.

              Collapsing it to a closed-by-default section was the other option and it is
              WORSE, because the Save button is not only a button: it carries the unsaved
              -changes state (the pulsing badge, the "(Unsaved Changes)" text). Hiding
              that behind a disclosure hides a warning. In the sticky toolbar it is now
              visible at ALL times, including while the page is scrolled, which the card
              never was.

              ml-auto pushes them to the trailing edge, so the view controls stay grouped
              at the leading edge where they were. */}
          <div className="ml-auto flex items-center gap-1 flex-shrink-0 pl-2">
            <button
              onClick={openLoadDialog}
              title="Load a saved session"
              className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-1.5 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Load
            </button>

            {isEditingExistingSession && (
              <button
                onClick={() => {
                  // "Save as New": clear the session metadata; the id is the parent's.
                  setSessionName('');
                  setDeploymentStatus('planning');
                  setIsEditingExistingSession(false);
                  setShowSaveDialog(true);
                }}
                disabled={invalidFields.length > 0}
                title={invalidFieldsTooltip ?? 'Save as a new session'}
                className="px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors flex items-center gap-1.5 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New
              </button>
            )}

            <button
              onClick={() => setShowSaveDialog(true)}
              disabled={invalidFields.length > 0}
              title={invalidFieldsTooltip ?? 'Save this session'}
              className={`relative px-3 py-1.5 rounded-md text-sm font-semibold text-white transition-colors flex items-center gap-1.5 flex-shrink-0 ${
                invalidFields.length > 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : hasUnsavedChanges
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {hasUnsavedChanges && (
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
              )}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {isEditingExistingSession ? 'Update' : 'Save'}
              {hasUnsavedChanges && <span className="text-xs font-normal">(unsaved)</span>}
            </button>
          </div>
        </div>


        {/* Generated Form Configuration Display - Full Width */}
        {generatedForm && (
          <div className="mb-6 w-full p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-green-900">
                    🎉 Spring Boot Form Generated Successfully!
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Your PDF has been transformed into a complete Spring Boot application
                  </p>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(
                        getApiUrl(`/api/pdf/${documentId}/download-form/${generatedForm.formId}`),
                        {
                          method: 'GET',
                          credentials: 'include',
                          headers: {
                            'Accept': 'application/zip',
                          },
                        }
                      );
                      
                      if (!response.ok) {
                        throw new Error(`Failed to download: ${response.status}`);
                      }
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.style.display = 'none';
                      a.href = url;
                      a.download = `${generatedForm.formName || 'SpringBootForm'}.zip`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (error) {
                      console.error('Error downloading ZIP file:', error);
                      setErrorMessage('Failed to download ZIP file. Please try again.');
                      setShowErrorNotification(true);
                      setTimeout(() => setShowErrorNotification(false), 6000);
                    }
                  }}
                  className="px-5 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center shadow-md hover:scale-105"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download ZIP
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(generatedForm, null, 2));
                    setSuccessMessage('Form configuration copied to clipboard!');
                    setShowSuccessNotification(true);
                    setTimeout(() => setShowSuccessNotification(false), 3000);
                  }}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy JSON
                </button>
                <button
                  onClick={() => setShowCloseConfirmation(true)}
                  className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Form Details */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Form Information
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Form ID:</span>
                    <span className="font-mono text-xs text-gray-900">{generatedForm.formId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="text-gray-900">{generatedForm.formName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fields:</span>
                    <span className="text-gray-900 font-semibold">{generatedForm.fields?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{generatedForm.status}</span>
                  </div>
                </div>
              </div>

              {/* Generated Classes */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  Generated Classes
                </h5>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Entity:</span>
                    <div className="font-mono text-xs text-purple-700 bg-purple-50 p-1 rounded mt-1">
                      {generatedForm.settings?.entityClassName}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Controller:</span>
                    <div className="font-mono text-xs text-purple-700 bg-purple-50 p-1 rounded mt-1">
                      {generatedForm.settings?.controllerClassName}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Table:</span>
                    <div className="font-mono text-xs text-purple-700 bg-purple-50 p-1 rounded mt-1">
                      {generatedForm.settings?.tableName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                  Configuration
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Package:</span>
                    <span className="font-mono text-xs text-gray-900">{generatedForm.settings?.basePackage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Validation:</span>
                    <span className={`px-2 py-1 text-xs rounded ${generatedForm.settings?.enableValidation ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {generatedForm.settings?.enableValidation ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Repository:</span>
                    <span className={`px-2 py-1 text-xs rounded ${generatedForm.settings?.generateRepository ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {generatedForm.settings?.generateRepository ? 'Generated' : 'Skip'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">API Docs:</span>
                    <span className={`px-2 py-1 text-xs rounded ${generatedForm.settings?.generateApiDocs ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {generatedForm.settings?.generateApiDocs ? 'Generated' : 'Skip'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Field Summary */}
            {generatedForm.fields && generatedForm.fields.length > 0 && (
              <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Generated Fields ({generatedForm.fields.length})
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {generatedForm.fields.slice(0, 6).map((field: any, index: number) => (
                    <div key={index} className="bg-gray-50 p-3 rounded border">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-900 text-sm">{field.label || field.name}</span>
                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                          field.type === 'EMAIL' ? 'bg-blue-100 text-blue-800' :
                          field.type === 'NUMBER' ? 'bg-green-100 text-green-800' :
                          field.type === 'DATE' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {field.type}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        {field.required && <span className="text-red-600 font-medium">Required • </span>}
                        Position: {Math.round(field.coordinates?.x || 0)}, {Math.round(field.coordinates?.y || 0)}
                      </div>
                    </div>
                  ))}
                  {generatedForm.fields.length > 6 && (
                    <div className="bg-gray-100 p-3 rounded border flex items-center justify-center">
                      <span className="text-gray-600 text-sm">
                        +{generatedForm.fields.length - 6} more fields
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

            {/* THE WORK AREA — ONE ROW, THREE SURFACES (W3). List, canvas,
                properties, left to right, all in the document flow at
                three-column widths; the drawer and tabbed arrangements at
                narrower widths are described at `workspaceMode`. DOM order is
                visual order, so the tab order follows for free, which is most
                of what clause 504.2.2 asks of an authoring tool's own UI (D30).

                THE 500px FLOOR IS ON THIS ROW, AND IT IS ONE FLOOR IN EVERY
                STATE. Scott: "The PDF Viewer of the actual PDF should be at
                least 500px tall, if not more." The band-era state machine that
                dropped this to 280px while properties were open is RETIRED
                with the band (see the header note): no surface borrows page
                height any more, so the promise holds unconditionally. 504
                rather than 500 because min-height lands on the scroller's
                BORDER box and its frame draws 2px top and bottom; a 500 floor
                measured 496. The `min(..., 100%)` second term is the old
                guard, kept: a floor that cannot be met inside the card yields
                instead of pushing the page into an overflow-hidden ancestor —
                the 203px-of-clipped-page defect this file already records.

                The floor is NOT on the canvas scroller below, and that was
                measured rather than reasoned: on the scroller it wins against
                the CARD as well and clips the page bottom. On the row it is
                bounded by the work area. MIN_CARD_PX at the top of this file
                is the same floor seen from outside the card; keep the two in
                step. An inline style, not a Tailwind class, so the rule stays
                legible beside the reason for it. */}
            <div
              className="flex gap-2 flex-1 min-h-0"
              style={{ minHeight: 'min(504px, 100%)' }}
            >

              {/* THE LEFT SURFACE. At three-column and drawer widths it is the
                  field list in its own cell; below ~1024px it is the shared
                  TABBED RAIL carrying both the list and the properties panel.
                  Either way the cell's WIDTH belongs to this component — the
                  splitter beside it is the control — and the child fills it
                  (see FieldListSidebar's inRail note). flex-shrink-0 because
                  this is navigation at a width the author chose; the canvas
                  takes the remainder. No tabindex on any wrapper, ever
                  (WCAG 2.1.1, see isTypingTarget) — the splitter is the one
                  argued exception, and it argues its case in its own file. */}
              {workspaceMode !== 'tabs' && selectedFields.length > 0 && (
                <>
                  <div
                    className="flex flex-col min-h-0 flex-shrink-0"
                    style={listCollapsed ? undefined : { width: listWidth }}
                  >
                    <FieldListSidebar
                      fields={selectedFields}
                      selectedFieldId={selectedFieldId}
                      onSelectField={handleSidebarFieldSelect}
                      badgeById={fieldBadgeById}
                      displayNameOf={getFieldDisplayName}
                      prefs={fieldSortPrefs}
                      onPrefsChange={onFieldSortPrefsChange}
                      readingOrderMode={readingOrderMode}
                      onReadingOrderModeChange={onReadingOrderModeChange}
                      invalidIds={invalidFieldIds}
                      onReorderFields={onFieldsReorder}
                      collapsed={listCollapsed}
                      onToggleCollapsed={toggleListCollapsed}
                      inRail
                    />
                  </div>
                  {/* No splitter beside a collapsed list: a separator that
                      resizes a 48px pill is a control that lies, and the pill
                      already returns the width wholesale. */}
                  {!listCollapsed && (
                    <WorkspaceSplitter
                      label="Resize the form fields list"
                      value={listWidth}
                      min={LIST_MIN_PX}
                      max={LIST_MAX_PX}
                      onChange={setListWidth}
                      panelSide="left"
                    />
                  )}
                </>
              )}

              {/* THE TABBED RAIL (< ~1024px): one cell, two labelled tabs —
                  the explicit, author-controlled trade W3 specifies where
                  three surfaces genuinely do not fit. Selecting a field shows
                  Properties; clearing the selection hands the rail back to
                  Fields (the effect at `activeTab`). This is also the phone
                  layout, deliberately NOT BROKEN rather than optimised: the
                  rail is capped at 42% of the row so the canvas keeps the
                  majority of the width at any window, nothing scrolls
                  sideways, and both tabs stay reachable.

                  Real tabs — role=tablist/tab/tabpanel, aria-selected, roving
                  tabindex, arrows to move — not styled buttons, because "an
                  explicit, announced trade" means announced in the markup a
                  screen reader actually reads, not just in the visual label.

                  The splitter still applies (aria reports the requested width;
                  the 42% cap is the crush guard the splitter's own max cannot
                  provide on a window this small). The collapse pills keep
                  their meaning inside the tabs — pfbFieldPropsCollapsed is
                  one key with one meaning at every width, see its note. */}
              {workspaceMode === 'tabs' && selectedFields.length > 0 && (
                <>
                  <div
                    className="flex flex-col min-h-0 flex-shrink-0 gap-1"
                    /* width + maxWidth rather than width: min(...): the same
                       arithmetic, and the split form survives CSS parsers that
                       reject math functions per-property (the test
                       environment's does, for width). */
                    style={{ width: listWidth, maxWidth: '42%' }}
                  >
                    <div
                      role="tablist"
                      aria-label="Fields and properties"
                      className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5 flex-shrink-0"
                      onKeyDown={handleTabListKeyDown}
                    >
                      <button
                        role="tab"
                        id={EDITOR_TAB_FIELDS_ID}
                        aria-selected={activeTab === 'fields'}
                        aria-controls={EDITOR_TABPANEL_FIELDS_ID}
                        tabIndex={activeTab === 'fields' ? 0 : -1}
                        onClick={() => setActiveTab('fields')}
                        className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          activeTab === 'fields' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Fields ({selectedFields.length})
                      </button>
                      <button
                        role="tab"
                        id={EDITOR_TAB_PROPS_ID}
                        aria-selected={activeTab === 'properties'}
                        aria-controls={EDITOR_TABPANEL_PROPS_ID}
                        tabIndex={activeTab === 'properties' ? 0 : -1}
                        onClick={() => setActiveTab('properties')}
                        className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          activeTab === 'properties' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Properties
                      </button>
                    </div>
                    {activeTab === 'fields' ? (
                      <div
                        role="tabpanel"
                        id={EDITOR_TABPANEL_FIELDS_ID}
                        aria-labelledby={EDITOR_TAB_FIELDS_ID}
                        className="flex flex-col min-h-0 flex-1"
                      >
                        <FieldListSidebar
                          fields={selectedFields}
                          selectedFieldId={selectedFieldId}
                          onSelectField={handleSidebarFieldSelect}
                          badgeById={fieldBadgeById}
                          displayNameOf={getFieldDisplayName}
                          prefs={fieldSortPrefs}
                          onPrefsChange={onFieldSortPrefsChange}
                          readingOrderMode={readingOrderMode}
                          onReadingOrderModeChange={onReadingOrderModeChange}
                          invalidIds={invalidFieldIds}
                          onReorderFields={onFieldsReorder}
                          collapsed={listCollapsed}
                          onToggleCollapsed={toggleListCollapsed}
                          inRail
                        />
                      </div>
                    ) : (
                      <div
                        role="tabpanel"
                        id={EDITOR_TABPANEL_PROPS_ID}
                        aria-labelledby={EDITOR_TAB_PROPS_ID}
                        className="flex flex-col min-h-0 flex-1"
                      >
                        {selectedFieldId && editingField ? (
                          renderPropertiesPanel(false)
                        ) : (
                          // The tab exists before a selection does, so it has
                          // to say what fills it rather than sitting empty.
                          <div className="text-xs text-gray-500 bg-white border-2 border-gray-200 rounded-xl p-3">
                            Select a field on the page, or in the Fields tab, to edit its properties here.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <WorkspaceSplitter
                    label="Resize the side panel"
                    value={listWidth}
                    min={LIST_MIN_PX}
                    max={LIST_MAX_PX}
                    onChange={setListWidth}
                    panelSide="left"
                  />
                </>
              )}

              {/* THE CANVAS CELL. `relative` is deliberate and new with W3: at
                  drawer widths the properties panel is absolutely positioned
                  against THIS box, which is what makes "overlays the canvas
                  edge only" a property of the layout rather than a promise —
                  the drawer physically cannot reach the field list, because
                  its positioning context ends where the canvas does.

                  Centering comes from the canvas wrapper's auto margins, NOT
                  justify-content: center - flex centering of an overflowing
                  child pushes half the overflow past the left edge where
                  scrollbars cannot reach it (scroll cannot go negative). Auto
                  margins centre when the page fits and collapse to zero when it
                  overflows, keeping the full width scrollable.

                  BOTH min-w-0 AND min-h-0 on the cell and on the scroller, and
                  none is decoration: each axis needs its own "may shrink below
                  content" opt-in, a missing one is silent, and this file has
                  shipped that bug twice. The 504px floor is NOT here - see the
                  row above for why putting it on the scroller clips the bottom
                  of the page. */}
              <div className="relative flex-1 min-w-0 min-h-0 flex flex-col">
                <div
                  ref={containerRef}
                  className="border-2 border-gray-300 rounded-xl overflow-auto bg-white shadow-lg flex-1 min-w-0 min-h-0 px-1"
                  style={{ display: 'flex', scrollBehavior: 'auto' }}
                >
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-2">Loading PDF...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-green-600 mb-2">
                  <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">       
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9     
   9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-gray-600">{error}</p>
                <p className="text-sm text-gray-500 mt-2">
                  PDF viewing will be implemented in the next step
                </p>
              </div>
            </div>
          )}

          {/* Canvas wrapper — canvas and overlays share the same positioning context */}
          {!isLoading && !error && pdfDoc && (
          <div ref={canvasWrapperRef} className="relative" style={{ display: 'inline-block', margin: '0 auto' }}>
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              className={isViewMode ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-crosshair'}
              style={{
                display: 'block'
              }}
            />

          {/* Current Selection Overlay */}
          {currentSelection && isSelecting && currentSelection.x !== undefined && currentSelection.y !== undefined && (
            <div
              className="absolute border-2 border-red-500 bg-red-200 bg-opacity-20"
              style={{
                left: currentSelection.x,
                top: currentSelection.y,
                width: currentSelection.width || 0,
                height: currentSelection.height || 0,
                pointerEvents: 'none'
              }}
            />
          )}

          {/* Field Selection Overlays - Only show fields for current page */}
          {selectedFields.filter(field => field.pageNumber === currentPage).map((field, index) => {
            // Special rendering for character block fields
            if (field.type === 'character_block' && field.characterBlockConfig && field.characterBlockConfig.blockWidth) {
              const { totalBlocks, blockSpacing, blockWidth, firstGap, firstGapPosition, secondGap, secondGapPosition } = field.characterBlockConfig;
              const scaledBlockWidth = blockWidth * scale;
              const scaledBlockSpacing = blockSpacing * scale;
              const scaledFirstGap = (firstGap || 0) * scale;
              const scaledSecondGap = (secondGap || 0) * scale;
              const blockHeight = field.height * scale;
              
              // Function to calculate block position with gaps
              const calculateBlockPosition = (blockIndex: number) => {
                let position = blockIndex * (scaledBlockWidth + scaledBlockSpacing);
                
                // Add first gap if this block is after the first gap position
                if (scaledFirstGap && firstGapPosition !== undefined && blockIndex > firstGapPosition) {
                  position += scaledFirstGap;
                }
                
                // Add second gap if this block is after the second gap position
                if (scaledSecondGap && secondGapPosition !== undefined && blockIndex > secondGapPosition) {
                  position += scaledSecondGap;
                }
                
                return position;
              };
              
              // Debug logging for character block positioning
              console.log(`Character Block Field Debug:`, {
                id: field.id,
                name: field.name,
                coordinates: { x: field.x, y: field.y },
                scaledPosition: { left: field.x * scale, top: field.y * scale },
                scale,
                originalConfig: { totalBlocks, blockSpacing, blockWidth, firstGap, firstGapPosition, secondGap, secondGapPosition },
                scaledConfig: { scaledBlockWidth, scaledBlockSpacing, scaledFirstGap, scaledSecondGap }
              });
              
              return (
                <div key={field.id} className="absolute" style={{
                  left: field.x * scale,
                  top: field.y * scale,
                }}>
                  {/* Field Label - Conditional Display */}
                  {(showFieldNames || selectedFieldId === field.id) && (
                    <div className={`absolute -top-6 left-0 text-xs px-1 rounded text-white ${
                      selectedFieldId === field.id ? 'bg-green-500' : 'bg-blue-500'
                    }`}>
                      {getFieldDisplayName(field, index)}
                    </div>
                  )}
                  
                  {/* Field Type Label */}
                  {(showFieldNames || selectedFieldId === field.id) && (
                    <div className="absolute -bottom-5 left-0 text-xs text-gray-600 bg-white px-1 rounded shadow">
                      {field.type}
                    </div>
                  )}
                  
                  {/* Individual Character Blocks */}
                  {Array.from({ length: totalBlocks }, (_, blockIndex) => {
                    // Get the character to display (from placeholder or default value)
                    const displayValue = field.formatHint || field.defaultValue || '';
                    const charToShow = displayValue[blockIndex] || '';
                    
                    return (
                      <div
                        key={blockIndex}
                        className={`absolute border-2 cursor-pointer flex items-center justify-center text-sm font-mono ${
                          selectedFieldId === field.id 
                            ? 'border-green-500 bg-green-100' 
                            : 'border-blue-500 bg-white hover:bg-blue-50'
                        }`}
                        style={{
                          left: calculateBlockPosition(blockIndex),
                          top: 0,
                          width: scaledBlockWidth,
                          height: blockHeight,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFieldId(field.id);
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedFieldId(field.id);
                          // Double-click OPENS the properties surface. Selecting a field is not
                          // enough while it is retracted: focus went to an input nobody could
                          // see, so the gesture appeared to do nothing. Expand first — and at
                          // tabbed widths show the Properties tab, since a dblclick on an
                          // already-selected field re-runs no selection effect — then focus.
                          if (propsCollapsed) togglePropsCollapsed();
                          if (workspaceMode === 'tabs') setActiveTab('properties');

                          // Double-click: jump into the properties sidebar name input
                          document.getElementById(FIELD_PROPS_NAME_INPUT_ID)?.focus();
                        }}
                        title={`${getFieldDisplayName(field, index)} (${field.type})`}
                      >
                        {charToShow && (
                          <span className={`${
                            field.formatHint && !field.defaultValue 
                              ? 'text-gray-400' 
                              : 'text-gray-800'
                          }`}>
                            {charToShow}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Special rendering for radio button fields
            if (field.type === 'radio_group' && field.radioGroupConfig && field.radioGroupConfig.options) {
              return (
                <div key={field.id} className="absolute" style={{
                  left: field.x * scale,
                  top: field.y * scale,
                }}>
                  {/* Field Label - Conditional Display */}
                  {(showFieldNames || selectedFieldId === field.id) && (
                    <div className={`absolute -top-6 left-0 text-xs px-1 rounded text-white ${
                      selectedFieldId === field.id ? 'bg-green-500' : 'bg-blue-500'
                    }`}>
                      {getFieldDisplayName(field, index)}
                    </div>
                  )}
                  
                  {/* Field Type Label */}
                  {(showFieldNames || selectedFieldId === field.id) && (
                    <div className="absolute -bottom-5 left-0 text-xs text-gray-600 bg-white px-1 rounded shadow">
                      radio group
                    </div>
                  )}

                  {/* Group Container */}
                  <div
                    className={`border-2 cursor-pointer relative ${
                      selectedFieldId === field.id 
                        ? 'border-green-500 bg-green-200 bg-opacity-20' 
                        : 'border-purple-500 bg-purple-200 bg-opacity-20 hover:bg-purple-300 hover:bg-opacity-30'
                    }`}
                    style={{
                      width: field.width * scale,
                      height: field.height * scale,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFieldId(field.id);
                      
                      // Only position if clicking on empty space (not on a radio button)
                      if (selectedFieldId === field.id && e.target === e.currentTarget) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const clickX = Math.round((e.clientX - rect.left) / scale);
                        const clickY = Math.round((e.clientY - rect.top) / scale);
                        
                        // Find first option without position
                        const optionToPosition = field.radioGroupConfig!.options.findIndex(opt => !opt.x && !opt.y);
                        if (optionToPosition !== -1) {
                          const updatedOptions = [...field.radioGroupConfig!.options];
                          // The click marks the CENTRE of the control, because
                          // the intent is to drop it onto a circle already
                          // printed on the form. Stored x,y stays the TOP-LEFT
                          // corner - which is what PDFGenerationService reads
                          // (centerX = x + width/2) and what every existing
                          // option means - so only new placements change, and
                          // nothing downstream has to be told.
                          //
                          // Without the offset the circle landed half its own
                          // size down and right of the click, which is the
                          // "off-centre" it looked like.
                          const RADIO_SIZE = 12;
                          updatedOptions[optionToPosition] = { 
                            ...updatedOptions[optionToPosition], 
                            x: clickX - RADIO_SIZE / 2, 
                            y: clickY - RADIO_SIZE / 2,
                            width: RADIO_SIZE,
                            height: RADIO_SIZE
                          };
                          
                          const updatedField = {
                            ...field,
                            radioGroupConfig: {
                              ...field.radioGroupConfig!,
                              options: updatedOptions
                            }
                          };
                          
                          if (onFieldUpdate) {
                            onFieldUpdate(updatedField);
                          }
                        }
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setSelectedFieldId(field.id);
                      // Double-click OPENS the properties surface — same reasoning as the
                      // character-block branch above.
                      if (propsCollapsed) togglePropsCollapsed();
                      if (workspaceMode === 'tabs') setActiveTab('properties');
                      
                      // Double-click: jump into the properties sidebar name input
                      document.getElementById(FIELD_PROPS_NAME_INPUT_ID)?.focus();
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    title={`${getFieldDisplayName(field, index)} (${field.type})`}
                  >
                    {/* Individual Radio Buttons */}
                    {field.radioGroupConfig.options.map((option, optionIndex) => {
                      const radioX = (option.x || 0) * scale;
                      const radioY = (option.y || 0) * scale;
                      const radioWidth = (option.width || 12) * scale;
                      const radioHeight = (option.height || 12) * scale;
                      
                      return (
                        <div
                          key={optionIndex}
                          className={`absolute border-2 rounded-full cursor-move flex items-center justify-center ${
                            selectedFieldId === field.id 
                              ? 'border-purple-600 bg-purple-100' 
                              : 'border-purple-400 bg-white hover:bg-purple-50'
                          }`}
                          style={{
                            left: radioX,
                            top: radioY,
                            width: radioWidth,
                            height: radioHeight,
                          }}
                          draggable={true}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData('text/plain', `radio-${optionIndex}`);
                          }}
                          onDrag={(e) => {
                            // Store drag position for onDragEnd
                            e.currentTarget.dataset.dragX = e.clientX.toString();
                            e.currentTarget.dataset.dragY = e.clientY.toString();
                          }}
                          onDragEnd={(e) => {
                            e.stopPropagation();
                            const containerRect = e.currentTarget.parentElement?.getBoundingClientRect();
                            if (containerRect) {
                              // Use stored drag coordinates for more accurate positioning
                              const dragX = parseInt(e.currentTarget.dataset.dragX || '0');
                              const dragY = parseInt(e.currentTarget.dataset.dragY || '0');
                              
                              // Account for container border (2px) and center the radio button
                              const borderOffset = 2;
                              const radioWidth = (option.width || 12) * scale;
                              const radioHeight = (option.height || 12) * scale;
                              
                              // Calculate position relative to container, accounting for border and centering
                              const relativeX = dragX - containerRect.left - borderOffset - (radioWidth / 2);
                              const relativeY = dragY - containerRect.top - borderOffset - (radioHeight / 2);
                              
                              const newX = Math.round(relativeX / scale);
                              const newY = Math.round(relativeY / scale);
                              
                              // Ensure position is within container bounds
                              const maxX = field.width - (option.width || 12);
                              const maxY = field.height - (option.height || 12);
                              const clampedX = Math.max(0, Math.min(newX, maxX));
                              const clampedY = Math.max(0, Math.min(newY, maxY));
                              
                              // Update this specific radio button's position
                              const updatedOptions = [...field.radioGroupConfig!.options];
                              updatedOptions[optionIndex] = { 
                                ...updatedOptions[optionIndex], 
                                x: clampedX, 
                                y: clampedY 
                              };
                              
                              // Update the field
                              const updatedField = {
                                ...field,
                                radioGroupConfig: {
                                  ...field.radioGroupConfig!,
                                  options: updatedOptions
                                }
                              };
                              
                              if (onFieldUpdate) {
                                onFieldUpdate(updatedField);
                              }
                              
                              // Clean up dataset
                              delete e.currentTarget.dataset.dragX;
                              delete e.currentTarget.dataset.dragY;
                            }
                          }}
                          title={`Drag to move: ${option.label} (${option.value})`}
                        >
                          {/* Radio button dot - only show when selected */}
                          {option.selected && (
                            <div className="w-1/2 h-1/2 bg-purple-600 rounded-full"></div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Radio button labels */}
                    {field.radioGroupConfig.options.map((option, optionIndex) => {
                      const radioX = (option.x || 0) * scale;
                      const radioY = (option.y || 0) * scale;
                      const radioWidth = (option.width || 12) * scale;
                      
                      // THE OPTION LABEL IS NOT DRAWN ON THE PAGE. It used to render beside each
                      // radio button here, which made it look like text PFB would print - and on an
                      // OVERLAY tool it never is: the words are already on the uploaded form, which
                      // is why the author is drawing a circle over a circle that already exists.
                      // Seeing it echoed on the canvas invites the author to "fix" a duplicate that
                      // does not exist in the file. Scott, 2026-09-18.
                      //
                      // The label is the ACCESSIBLE name for that option, and since D23 was fixed it
                      // reaches the file: it is written into the group's /Opt array, one entry per
                      // kid in /Kids order, which ISO 32000-1 Table 227 defines as the presentable
                      // form of each on state. A per-kid /TU would be out of spec (clause 12.7.3.2)
                      // and the export value stays on the /AP /N on-state name. It still belongs in
                      // the title attribute as well, where it names the control for a mouse user
                      // without painting anything - see the wrapper above, which already carries it.
                      return null;
                    })}
                  </div>
                </div>
              );
            }
            
            // Standard field rendering for non-character-block fields
            return (
              <div
                key={field.id}
                className={`absolute border-2 cursor-pointer ${
                  selectedFieldId === field.id 
                    ? 'border-green-500 bg-green-200 bg-opacity-40' 
                    : 'border-blue-500 bg-blue-200 bg-opacity-30 hover:bg-blue-300 hover:bg-opacity-40'
                }`}
                style={{
                  left: field.x * scale,
                  top: field.y * scale,
                  width: field.width * scale,
                  height: field.height * scale,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFieldId(field.id);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setSelectedFieldId(field.id);
                  // Double-click OPENS the properties surface — same reasoning as the
                  // character-block branch above.
                  if (propsCollapsed) togglePropsCollapsed();
                  if (workspaceMode === 'tabs') setActiveTab('properties');
                  
                  // Double-click: jump into the properties sidebar name input
                  document.getElementById(FIELD_PROPS_NAME_INPUT_ID)?.focus();
                }}
                title={`${getFieldDisplayName(field, index)} (${field.type})`}
              >
                {/* Field Label - Conditional Display */}
                {(showFieldNames || selectedFieldId === field.id) && (
                  <div className={`absolute -top-6 left-0 text-xs px-1 rounded text-white ${
                    selectedFieldId === field.id ? 'bg-green-500' : 'bg-blue-500'
                  }`}>
                    {getFieldDisplayName(field, index)}
                  </div>
                )}
                
                {/* Checkbox content display */}
                {field.type === 'checkbox' && (
                  <div className="flex items-center justify-center h-full w-full text-gray-800 font-bold text-lg">
                    {field.defaultValue === 'true' ? 'X' : ''}
                  </div>
                )}
                
                {/* Select field content display */}
                {field.type === 'select' && (
                  <div className="flex items-center justify-between px-2 h-full text-gray-800 text-sm">
                    <span className="truncate">
                      {field.selectConfig?.options?.find(opt => opt.selected)?.label || 'Select...'}
                    </span>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                )}
                
                {/* Other field types content display */}
                {field.type !== 'checkbox' && field.type !== 'select' && field.defaultValue && (
                  <div className="flex items-center px-2 h-full text-gray-800 text-sm truncate">
                    {field.defaultValue}
                  </div>
                )}
                
                {/* Field Type Label */}
                {(showFieldNames || selectedFieldId === field.id) && (
                  <div className="absolute -bottom-5 left-0 text-xs text-gray-600 bg-white px-1 rounded shadow">
                    {field.type}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          )}
          {/* End canvas wrapper and conditional */}
          </div>
          {/* End containerRef */}

          {/* THE PROPERTIES DRAWER (~1024–1440px): the panel over the CANVAS
              EDGE ONLY, inside the canvas cell's positioning context so it
              cannot reach the field list — which REMAINS OPEN, the hard rule
              at this width (W3). Occlusion returns here deliberately and
              bounded: at this width the alternative to covering a slice of
              canvas is hiding the keyboard path, which is DEFECT-84 itself.
              The dismissal obligations that come with covering content come
              with it — Esc closes the drawer (the window handler's second
              job), the open is announced in the drawer's own live region,
              and close returns focus to the field's list row.

              The width is the same propsWidth the splitter controls, capped
              at 85% of the cell so even End-at-the-separator leaves a strip
              of page visible under the drawer's edge. The splitter rides the
              drawer's left edge. */}
          {workspaceMode === 'drawer' && selectedFieldId && editingField && !propsCollapsed && (
            <div
              className="absolute inset-y-0 right-0 z-30 flex"
              /* width + maxWidth, not width: min(...) — see the tabbed rail's
                 note on CSS parsers that reject math functions per-property. */
              style={{ width: propsWidth, maxWidth: '85%' }}
            >
              <WorkspaceSplitter
                label="Resize the field properties panel"
                value={propsWidth}
                min={PROPS_MIN_PX}
                max={PROPS_MAX_PX}
                onChange={setPropsWidth}
                panelSide="right"
              />
              <div className="flex flex-col min-h-0 flex-1">
                {renderPropertiesPanel(false)}
              </div>
            </div>
          )}
          {/* The drawer's retracted pill, floated at the same edge the drawer
              opens from so the collapse control and its effect share a place. */}
          {workspaceMode === 'drawer' && selectedFieldId && editingField && propsCollapsed && (
            <div className="absolute top-2 right-2 z-30 flex">
              {renderPropertiesPanel(false)}
            </div>
          )}

          </div>
          {/* End canvas cell */}

          {/* THE PROPERTIES RAIL (>= ~1440px): the third persistent column,
              in the flow, full workspace height, scrolling internally. This
              cell's width is the splitter's value; retracted, the cell takes
              the pill's own 48px and the rest genuinely returns to the page.
              The internal grid widens to two columns only when the author
              has pulled the rail past PROPS_TWO_COLUMN_PX — the panel's
              twoColumn note owns that argument. */}
          {workspaceMode === 'columns' && selectedFieldId && editingField && (
            <>
              {!propsCollapsed && (
                <WorkspaceSplitter
                  label="Resize the field properties panel"
                  value={propsWidth}
                  min={PROPS_MIN_PX}
                  max={PROPS_MAX_PX}
                  onChange={setPropsWidth}
                  panelSide="right"
                />
              )}
              <div
                className="flex flex-col min-h-0 flex-shrink-0"
                style={propsCollapsed ? undefined : { width: propsWidth }}
              >
                {renderPropertiesPanel(propsWidth >= PROPS_TWO_COLUMN_PX)}
              </div>
            </>
          )}

          </div>
          {/* End work area row */}

          {/* Controls Section - Full Width Below PDF Viewer */}
          <div className="w-full">
            <div className="space-y-8">

              {/* Field properties live in FieldPropertiesSidebar, beside the page */}

          {/* Form Fields list lives in FieldListSidebar, in the flow left of the page */}
            </div>
          </div>
        </div>
        </div>

        {/* Close Confirmation Dialog */}
        {showCloseConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Close</h3>
                  <p className="text-sm text-gray-600">Are you sure you want to close this view?</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6">
                Your work will remain saved, but the generated form results will be closed. 
                You can always regenerate the form if needed.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setGeneratedForm(null);
                    setShowCloseConfirmation(false);
                  }}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 font-medium"
                >
                  Yes, Close
                </button>
                <button
                  onClick={() => setShowCloseConfirmation(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Session Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-2xl max-h-[90vh] flex flex-col">
              {/* Scrolling body. min-h-0 is required: without it a flex child will not
                  shrink below its content and this scrollbar never engages - the same
                  trap that made the canvas container overflow earlier today. */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isEditingExistingSession ? 'Update Session' : 'Save Session'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isEditingExistingSession ? 'Save changes to existing session' : 'Save your work in progress'}
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                {isEditingExistingSession 
                  ? 'Add a comment about the changes you\'re saving to this session.'
                  : 'Enter a name for this editing session. You can reload it later to continue your work.'
                }
              </p>
              
              {/* PDF Source File Display */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">PDF Source</p>
                    <p className="text-sm text-blue-700 font-mono">
                      {originalFilename?.replace(/\.pdf\.pdf$/i, '.pdf') || 'Unknown file'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Name <span className="text-red-500">*</span> {isEditingExistingSession && <span className="text-xs text-gray-500">(editable)</span>}
                  </label>
                  <input
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && sessionName.trim() && !isSavingSession) {
                        e.preventDefault();
                        handleSaveSession();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter a descriptive name..."
                    maxLength={50}
                    autoFocus={!isEditingExistingSession}
                  />
                </div>
                
                {isEditingExistingSession && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Change Comment <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={changeComment}
                      onChange={(e) => setChangeComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                      placeholder="Describe what changes you made in this update..."
                      rows={3}
                      maxLength={200}
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required for updates • {200 - changeComment.length} characters remaining
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deployment Status
                  </label>
                  <select
                    value={deploymentStatus}
                    onChange={(e) => setDeploymentStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="planning">🗂️ Planning - Initial concept and design</option>
                    <option value="development">⚙️ Development - Active coding and implementation</option>
                    <option value="testing">🧪 Testing - Quality assurance and validation</option>
                    <option value="release">🚀 Release - Ready for production deployment</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose the current stage of your form development
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Description (Optional)
                  </label>
                  <textarea
                    value={sessionDescription}
                    onChange={(e) => setSessionDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                    placeholder="Add notes about this session, what you're working on, or any important details..."
                    rows={3}
                    maxLength={400}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {400 - sessionDescription.length} characters remaining
                  </p>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Session includes:</strong> {selectedFields.length} fields, page {currentPage}, zoom {Math.round(scale * 100)}%
                  </p>
                </div>
              </div>
              </div>
              {/* ACTIONS STAY PUT. This row used to sit inside the panel's own
                  overflow-y-auto, so on a short window the user had to scroll the whole
                  dialog to discover that Update Session and Cancel existed at all.
                  Fixed footer, scrolling body: the standard dialog shape, and the one a
                  keyboard user also benefits from because the actions are always on
                  screen when focus reaches them. */}
              <div className="flex space-x-3 flex-none px-6 py-4 border-t border-gray-200">
                <button
                  onClick={handleSaveSession}
                  disabled={isSavingSession || !sessionName.trim() || (isEditingExistingSession && !changeComment.trim()) || invalidFields.length > 0}
                  title={invalidFieldsTooltip}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors duration-200 font-medium flex items-center justify-center ${
                    isSavingSession || !sessionName.trim() || (isEditingExistingSession && !changeComment.trim()) || invalidFields.length > 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  {isSavingSession ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    isEditingExistingSession ? 'Update Session' : 'Save Session'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setChangeComment('');
                    // Don't reset session data when canceling existing session edits
                    if (!isEditingExistingSession) {
                      setSessionName('');
                      setDeploymentStatus('planning');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Notification */}
        {showSuccessNotification && (
          <div className="fixed top-4 right-4 z-50 animate-slide-down">
            <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-xl flex items-center space-x-3 max-w-md">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold">Success!</p>
                {/*
                  Rendered as TEXT, never as HTML. successMessage is built from a
                  user-typed session name (see the save and load handlers), so
                  dangerouslySetInnerHTML made this a stored-XSS sink: a session named
                  with an <img onerror=...> payload executed in the browser of any
                  colleague in the same tenant who loaded it. All four setSuccessMessage
                  call sites pass plain text, so nothing here ever needed HTML.
                */}
                <div className="text-sm text-green-100">{successMessage}</div>
              </div>
              <button
                onClick={() => setShowSuccessNotification(false)}
                className="flex-shrink-0 text-green-100 hover:text-white transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {showErrorNotification && (
          <div className="fixed top-4 right-4 z-50 animate-slide-down">
            <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-xl flex items-center space-x-3 max-w-md">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold">Error!</p>
                <p className="text-sm text-red-100">{errorMessage}</p>
              </div>
              <button
                onClick={() => setShowErrorNotification(false)}
                className="flex-shrink-0 text-red-100 hover:text-white transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Load Session Dialog - Using new tree control component */}
        <LoadSessionDialog
          isOpen={showLoadDialog}
          isLoading={isLoadingSessions}
          sessions={availableSessions}
          onClose={() => setShowLoadDialog(false)}
          onLoadSession={handleLoadSession}
        />

      </div>
    </div>
    );
  };

  export default PDFViewer;